// Source language of the codebase: English.
// Each user sees the UI in their own preferred_lang (he / ur / tl / en).
// Translations are additive overlays on top of the English source strings.
//
// Usage: t('Home') → 'Home' (en) | 'הבית' (he) | 'گھر' (ur) | 'Bahay' (tl)

export type Lang = 'en' | 'he' | 'ur' | 'tl';

const LANG_KEY = 'fm.lang';

export function getLang(): Lang {
  // English is the source language. We only switch when the user (or their
  // server-side preferred_lang on signin) explicitly picks another lang.
  // No browser-language sniffing — a Hebrew-locale browser still gets English
  // by default, matching the architectural intent.
  const raw = localStorage.getItem(LANG_KEY);
  if (raw === 'en' || raw === 'he' || raw === 'ur' || raw === 'tl') return raw;
  return 'en';
}

export function setLang(l: Lang): void {
  localStorage.setItem(LANG_KEY, l);
  document.documentElement.lang = l;
  document.documentElement.dir = isRtl(l) ? 'rtl' : 'ltr';
  // No event bus needed — callers are expected to re-render or refresh; in
  // practice the lang is set during signin which navigates immediately after.
}

export function isRtl(l: Lang = getLang()): boolean {
  return l === 'he' || l === 'ur';
}

// Translations: source-string → per-lang override.
// English source strings are the keys. Missing translations fall back to the
// source string (English).
const T: Record<string, Partial<Record<Lang, string>>> = {
  // Brand / tagline
  'family · manage': { he: 'משפחה · ניהול', ur: 'خاندان · انتظام', tl: 'pamilya · pangasiwaan' },
  'The operating system for your household.': {
    he: 'המערכת ההפעלה של הבית.',
    ur: 'آپ کے گھر کا آپریٹنگ سسٹم۔',
    tl: 'Ang operating system ng inyong tahanan.',
  },
  'Sign in as owner': {
    he: 'כניסה כבעלים',
    ur: 'مالک کے طور پر سائن ان',
    tl: 'Mag-sign in bilang may-ari',
  },
  'Start a new household': {
    he: 'פתח משק בית חדש',
    ur: 'نیا گھرانہ کھولیں',
    tl: 'Magbukas ng bagong sambahayan',
  },
  'Sign in as staff': {
    he: 'כניסה כעובד',
    ur: 'عملہ کے طور پر سائن ان',
    tl: 'Mag-sign in bilang tauhan',
  },
  'Start your household': {
    he: 'פתח את משק הבית',
    ur: 'اپنا گھرانہ کھولیں',
    tl: 'Buksan ang sambahayan',
  },
  'One app for the whole family — Hebrew, English, اردو, Tagalog': {
    he: 'אפליקציה אחת לכל המשפחה — עברית, אנגלית, اردو, Tagalog',
    ur: 'پورے خاندان کے لیے ایک ایپ — Hebrew, English, اردو, Tagalog',
    tl: 'Isang app para sa buong pamilya — Hebrew, English, اردو, Tagalog',
  },
  'Staff?': { he: 'עובד?', ur: 'عملہ؟', tl: 'Tauhan?' },
  'Sign in here': { he: 'כניסה כאן', ur: 'یہاں سائن ان کریں', tl: 'Mag-sign in dito' },
  'Same sign-in for owners and staff — we figure out who you are.': {
    he: 'אותה כניסה לבעלים ולעובדים — אנחנו מזהים מי אתה לפי השם.',
    ur: 'مالکان اور عملہ کے لیے ایک ہی سائن ان — ہم خود پہچان لیں گے۔',
    tl: 'Iisang sign-in para sa may-ari at tauhan — kami ang aalam kung sino ka.',
  },
  'Email or username': {
    he: 'אימייל או שם משתמש',
    ur: 'ای میل یا صارف نام',
    tl: 'Email o username',
  },
  'This username exists in more than one household. Add the household code to continue.': {
    he: 'שם המשתמש קיים ביותר ממשק בית אחד. הוסף קוד בית כדי להמשיך.',
    ur: 'یہ صارف نام ایک سے زائد گھرانوں میں موجود ہے۔ آگے بڑھنے کے لیے گھریلو کوڈ شامل کریں۔',
    tl: 'Umiiral ang username na ito sa higit sa isang sambahayan. Magdagdag ng household code.',
  },
  Show: { he: 'הצג', ur: 'دکھائیں', tl: 'Ipakita' },
  Hide: { he: 'הסתר', ur: 'چھپائیں', tl: 'Itago' },

  // Nav
  Home: { he: 'הבית', ur: 'گھر', tl: 'Bahay' },
  Tasks: { he: 'משימות', ur: 'کام', tl: 'Mga gawain' },
  'New task': { he: 'משימה חדשה', ur: 'نیا کام', tl: 'Bagong task' },
  'Quick command': { he: 'פקודה מהירה', ur: 'فوری حکم', tl: 'Mabilis na utos' },
  Staff: { he: 'עובדים', ur: 'عملہ', tl: 'Tauhan' },
  Money: { he: 'כסף', ur: 'پیسے', tl: 'Pera' },
  Expenses: { he: 'הוצאות', ur: 'اخراجات', tl: 'Mga gastos' },
  Memory: { he: 'זכרון הבית', ur: 'گھر کی یادداشت', tl: 'Alaala' },
  Today: { he: 'היום', ur: 'آج', tl: 'Ngayong araw' },
  Profile: { he: 'פרופיל', ur: 'پروفائل', tl: 'Profile' },
  'Sign out': { he: 'יציאה', ur: 'سائن آؤٹ', tl: 'Mag-sign out' },
  Primary: { he: 'ניווט ראשי', ur: 'بنیادی نیویگیشن', tl: 'Pangunahing nabigasyon' },
  Language: { he: 'שפה', ur: 'زبان', tl: 'Wika' },

  // Common actions
  Save: { he: 'שמור', ur: 'محفوظ کریں', tl: 'I-save' },
  Cancel: { he: 'ביטול', ur: 'منسوخ', tl: 'Kanselahin' },
  Continue: { he: 'המשך', ur: 'جاری رکھیں', tl: 'Magpatuloy' },
  Back: { he: 'חזרה', ur: 'واپس', tl: 'Bumalik' },
  Approve: { he: 'אשר', ur: 'منظور کریں', tl: 'Aprubahan' },
  Reject: { he: 'דחה', ur: 'رد کریں', tl: 'Tanggihan' },
  Delete: { he: 'מחק', ur: 'حذف کریں', tl: 'Burahin' },
  Edit: { he: 'ערוך', ur: 'ترمیم کریں', tl: 'I-edit' },
  Send: { he: 'שלח', ur: 'بھیجیں', tl: 'Ipadala' },
  Start: { he: 'התחל', ur: 'شروع کریں', tl: 'Simulan' },
  Done: { he: 'בוצע', ur: 'مکمل', tl: 'Tapos' },
  Archive: { he: 'ארכב', ur: 'محفوظ', tl: 'I-archive' },
  Sign: { he: 'כניסה', ur: 'سائن ان', tl: 'Mag-sign in' },
  Loading: { he: 'טוען…', ur: 'لوڈ ہو رہا ہے…', tl: 'Naglo-load…' },
  'Loading failed': { he: 'טעינה נכשלה', ur: 'لوڈ ناکام', tl: 'Bigo ang pag-load' },
  Failed: { he: 'נכשל', ur: 'ناکام', tl: 'Bigo' },
  Close: { he: 'סגור', ur: 'بند کریں', tl: 'Isara' },
  'Approve and create': {
    he: 'אשר וצור',
    ur: 'منظور کریں اور بنائیں',
    tl: 'Aprubahan at gawin',
  },
  'Reject proposal': {
    he: 'דחה הצעה',
    ur: 'تجویز مسترد کریں',
    tl: 'Tanggihan ang panukala',
  },
  'Delete receipt': { he: 'מחק קבלה', ur: 'رسید حذف کریں', tl: 'Burahin ang resibo' },
  'Delete this receipt permanently?': {
    he: 'למחוק את הקבלה הזו לצמיתות?',
    ur: 'یہ رسید مستقل طور پر حذف کریں؟',
    tl: 'Permanenteng burahin ang resibong ito?',
  },
  'Receipt photo': { he: 'תצלום קבלה', ur: 'رسید کی تصویر', tl: 'Larawan ng resibo' },

  // Owner home
  'My household': { he: 'הבית שלי', ur: 'میرا گھر', tl: 'Aking sambahayan' },
  'Needs your attention': { he: 'דורש את תשומת לבך', ur: 'آپ کی توجہ درکار', tl: 'Kailangan mo' },
  'Awaiting approval': {
    he: 'ממתין לאישור',
    ur: 'منظوری کا منتظر',
    tl: 'Hinihintay ang pag-apruba',
  },
  'In progress': { he: 'בביצוע', ur: 'جاری', tl: 'Ginagawa' },
  'Stuck or overdue': { he: 'תקוע / חורג', ur: 'پھنسا / دیر', tl: 'Tigil o lampas' },
  'Active staff': { he: 'צוות פעיל', ur: 'فعال عملہ', tl: 'Aktibong tauhan' },
  'Smart suggestions': { he: 'הצעות חכמות', ur: 'سمارٹ تجاویز', tl: 'Mga mungkahi' },
  Suggestion: { he: 'הצעה', ur: 'تجویز', tl: 'Mungkahi' },
  "Who's working on what": {
    he: 'מי עובד על מה',
    ur: 'کون کیا کر رہا ہے',
    tl: 'Sino ang gumagawa ng ano',
  },
  'Everything under control.': {
    he: 'הכל תחת שליטה.',
    ur: 'سب کنٹرول میں ہے۔',
    tl: 'Maayos ang lahat.',
  },
  'Nothing scheduled today.': {
    he: 'היום ריק.',
    ur: 'آج کوئی پروگرام نہیں۔',
    tl: 'Walang naka-schedule ngayong araw.',
  },
  'No active staff.': {
    he: 'אין עובדים פעילים.',
    ur: 'کوئی فعال عملہ نہیں۔',
    tl: 'Walang aktibong tauhan.',
  },
  Now: { he: 'עכשיו', ur: 'ابھی', tl: 'Ngayon' },
  Waiting: { he: 'בהמתנה', ur: 'انتظار میں', tl: 'Naghihintay' },
  Overdue: { he: 'חורג', ur: 'دیر', tl: 'Lampas na' },
  'Outstanding owed': { he: 'חוב לעובדים', ur: 'بقایا واجبات', tl: 'Hindi pa nababayaran' },
  'Pending receipts': { he: 'קבלות ממתינות', ur: 'زیر التواء رسیدیں', tl: 'Mga rebong nakabinbin' },
  'Active loans': { he: 'הלוואות פעילות', ur: 'فعال قرضے', tl: 'Aktibong utang' },
  Deadline: { he: 'דדליין', ur: 'آخری تاریخ', tl: 'Deadline' },

  // Staff today
  'Right now': { he: 'עכשיו', ur: 'ابھی', tl: 'Ngayon mismo' },
  'Up next': { he: 'הבא', ur: 'اگلا', tl: 'Susunod' },
  Upcoming: { he: 'בקרוב', ur: 'آنے والے', tl: 'Paparating' },
  Tomorrow: { he: 'מחר', ur: 'کل', tl: 'Bukas' },
  'Waiting on me': { he: 'מחכה ממני', ur: 'مجھ سے انتظار', tl: 'Hinihintay sa akin' },
  'Done today': { he: 'בוצע היום', ur: 'آج مکمل', tl: 'Tapos ngayon' },
  'Nothing to do right now.': {
    he: 'אין מה לעשות עכשיו.',
    ur: 'ابھی کچھ نہیں کرنا۔',
    tl: 'Wala munang gagawin.',
  },
  "I'm starting": { he: 'מתחיל', ur: 'شروع کر رہا ہوں', tl: 'Sisimulan ko' },
  'Send proof': { he: 'שלח הוכחה', ur: 'ثبوت بھیجیں', tl: 'Magpadala ng patunay' },
  'Mark done': {
    he: 'סיימתי, שלח לאישור',
    ur: 'مکمل، منظوری بھیجیں',
    tl: 'Tapos, ipasa sa approval',
  },
  'Photo (camera)': { he: 'תמונה (מצלמה)', ur: 'تصویر (کیمرہ)', tl: 'Larawan (kamera)' },
  'Voice note': { he: 'הקלטה קולית', ur: 'وائس نوٹ', tl: 'Voice note' },
  'Stop recording': { he: 'עצור הקלטה', ur: 'ریکارڈنگ روکیں', tl: 'Itigil ang pag-record' },
  'Voice ready · tap to re-record': {
    he: 'הקלטה מוכנה · הקש להקלטה מחדש',
    ur: 'تیار · دوبارہ ریکارڈ',
    tl: 'Tapos · pindutin para ulitin',
  },
  'Short note (optional)': {
    he: 'תיאור קצר (לא חובה)',
    ur: 'مختصر نوٹ (اختیاری)',
    tl: 'Maikling tala (opsyonal)',
  },

  // Operation detail
  History: { he: 'היסטוריה', ur: 'تاریخ', tl: 'Kasaysayan' },
  'Nothing happened yet.': {
    he: 'עוד לא קרה כלום.',
    ur: 'ابھی کچھ نہیں ہوا۔',
    tl: 'Wala pang nangyari.',
  },
  Add: { he: 'הוסף', ur: 'شامل کریں', tl: 'Magdagdag' },
  'Write a message tied to the task…': {
    he: 'כתוב הודעה צמודה למשימה…',
    ur: 'کام کے ساتھ پیغام لکھیں…',
    tl: 'Magsulat ng mensahe sa gawain…',
  },
  Note: { he: 'הערה', ur: 'نوٹ', tl: 'Tala' },
  Question: { he: 'שאלה', ur: 'سوال', tl: 'Tanong' },
  Answer: { he: 'תשובה', ur: 'جواب', tl: 'Sagot' },
  Issue: { he: 'בעיה', ur: 'مسئلہ', tl: 'Problema' },
  Reminder: { he: 'תזכורת', ur: 'یاد دہانی', tl: 'Paalala' },
  Instruction: { he: 'הוראה', ur: 'ہدایت', tl: 'Tagubilin' },
  Approval: { he: 'אישור', ur: 'منظوری', tl: 'Pag-apruba' },
  Rejection: { he: 'דחייה', ur: 'انکار', tl: 'Pagtanggi' },
  Proof: { he: 'הוכחה', ur: 'ثبوت', tl: 'Patunay' },
  Attachment: { he: 'קובץ מצורף', ur: 'منسلکہ', tl: 'Kalakip' },
  'Assigned to': { he: 'עובד אחראי', ur: 'تفویض', tl: 'Italaga kay' },
  Priority: { he: 'עדיפות', ur: 'ترجیح', tl: 'Priyoridad' },
  'Scheduled for': { he: 'מתוכנן ל', ur: 'وقت', tl: 'Naka-schedule sa' },
  Created: { he: 'נוצרה', ur: 'بنایا', tl: 'Ginawa' },
  'Not assigned': { he: 'לא משויך', ur: 'تفویض نہیں', tl: 'Hindi italaga' },
  'Cancel task': { he: 'בטל משימה', ur: 'کام منسوخ کریں', tl: 'Kanselahin' },
  Publish: { he: 'פרסם', ur: 'شائع کریں', tl: 'I-publish' },
  'Why reject?': { he: 'סיבת דחייה?', ur: 'انکار کی وجہ؟', tl: 'Bakit tanggihan?' },
  'Why cancel?': { he: 'סיבת ביטול?', ur: 'منسوخی کی وجہ؟', tl: 'Bakit kanselahin?' },

  // Priority labels (3-level: normal | important | emergency, matches schema)
  Normal: { he: 'רגיל', ur: 'عام', tl: 'Karaniwan' },
  Important: { he: 'חשוב', ur: 'اہم', tl: 'Mahalaga' },
  Emergency: { he: 'חירום', ur: 'ہنگامی', tl: 'Emerhensiya' },

  // State labels (full set)
  Draft: { he: 'טיוטה', ur: 'مسودہ', tl: 'Burador' },
  Scheduled: { he: 'מתוכנן', ur: 'شیڈولڈ', tl: 'Naka-schedule' },
  Sent: { he: 'נשלח', ur: 'بھیجا', tl: 'Naipadala' },
  Seen: { he: 'נראה', ur: 'دیکھا', tl: 'Nakita' },
  'Waiting for proof': { he: 'ממתין להוכחה', ur: 'ثبوت کا انتظار', tl: 'Hinihintay ang patunay' },
  Approved: { he: 'אושר', ur: 'منظور', tl: 'Aprubado' },
  Rejected: { he: 'נדחה', ur: 'مسترد', tl: 'Tinanggihan' },
  Cancelled: { he: 'בוטל', ur: 'منسوخ', tl: 'Kinansela' },
  'Needs proof': { he: 'דורש הוכחה', ur: 'ثبوت چاہئے', tl: 'Kailangan ng patunay' },

  // Auth screens
  'Sign in': { he: 'כניסה', ur: 'سائن ان', tl: 'Mag-sign in' },
  'Sign up': { he: 'הרשמה', ur: 'سائن اپ', tl: 'Mag-sign up' },
  Email: { he: 'אימייל', ur: 'ای میل', tl: 'Email' },
  Password: { he: 'סיסמה', ur: 'پاس ورڈ', tl: 'Password' },
  'Household code': { he: 'קוד הבית', ur: 'گھریلو کوڈ', tl: 'Code ng sambahayan' },
  'Only you should know it — not the person who set up your account.': {
    he: 'רק אתה צריך לדעת אותה — לא מי שפתח לך את החשבון.',
    ur: 'صرف آپ کو معلوم ہونی چاہیے — وہ نہیں جس نے آپ کا اکاؤنٹ بنایا۔',
    tl: 'Ikaw lang ang dapat nakakaalam — hindi ang nag-set up ng account mo.',
  },
  'Pick a new password before you continue.': {
    he: 'צריך לבחור סיסמה חדשה לפני שתמשיך.',
    ur: 'جاری رکھنے سے پہلے نیا پاس ورڈ منتخب کریں۔',
    tl: 'Pumili ng bagong password bago magpatuloca.',
  },
  'Sign-in required': {
    he: 'נדרשת התחברות מחדש',
    ur: 'دوبارہ سائن ان درکار',
    tl: 'Kailangan mag-sign in muli',
  },

  // Profile
  'Change name': { he: 'שינוי שם', ur: 'نام بدلیں', tl: 'Palitan ang pangalan' },
  'Change password': { he: 'שינוי סיסמה', ur: 'پاس ورڈ بدلیں', tl: 'Palitan ang password' },
  'Profile picture': { he: 'תמונת פרופיל', ur: 'پروفائل تصویر', tl: 'Larawan' },
  'Edit photo': { he: 'ערוך תמונה', ur: 'تصویر تبدیل کریں', tl: 'I-edit ang larawan' },
  Remove: { he: 'הסר', ur: 'ہٹا دیں', tl: 'Alisin' },
  'Good morning': { he: 'בוקר טוב', ur: 'صبح بخیر', tl: 'Magandang umaga' },
  'Good afternoon': { he: 'צהריים טובים', ur: 'دوپہر بخیر', tl: 'Magandang hapon' },
  'Good evening': { he: 'ערב טוב', ur: 'شام بخیر', tl: 'Magandang gabi' },
  'Display language': { he: 'שפת ממשק', ur: 'انٹرفیس زبان', tl: 'Wika' },
  'Current password': { he: 'סיסמה נוכחית', ur: 'موجودہ پاس ورڈ', tl: 'Kasalukuyang password' },
  'New?': { he: 'חדש?', ur: 'نیا؟', tl: 'Bago?' },
  'Full name': { he: 'שם מלא', ur: 'پورا نام', tl: 'Buong pangalan' },
  'Family name': { he: 'שם משפחה', ur: 'خاندانی نام', tl: 'Pangalan ng pamilya' },
  'Household name': { he: 'שם הבית', ur: 'گھر کا نام', tl: 'Pangalan ng sambahayan' },
  Country: { he: 'מדינה', ur: 'ملک', tl: 'Bansa' },
  Currency: { he: 'מטבע', ur: 'کرنسی', tl: 'Pera' },
  'Household settings': {
    he: 'הגדרות משק בית',
    ur: 'گھرانے کی ترتیبات',
    tl: 'Mga setting ng sambahayan',
  },
  'All amounts on the Expenses screen are shown in this currency.': {
    he: 'כל הסכומים במסך ההוצאות מוצגים במטבע הזה.',
    ur: 'اخراجات کی اسکرین پر تمام رقمیں اس کرنسی میں دکھائی جاتی ہیں۔',
    tl: 'Lahat ng halaga sa Expenses screen ay ipapakita sa currency na ito.',
  },
  Timezone: { he: 'אזור זמן', ur: 'ٹائم زون', tl: 'Time zone' },
  Hebrew: { he: 'עברית', ur: 'عبرانی', tl: 'Hebreo' },
  Urdu: { he: 'اردو', ur: 'اردو', tl: 'Urdu' },
  English: { he: 'אנגלית', ur: 'انگریزی', tl: 'Ingles' },
  Tagalog: { he: 'טגלוגית', ur: 'ٹیگالوگ', tl: 'Tagalog' },
  Routines: { he: 'שגרות', ur: 'روٹینز', tl: 'Mga rutina' },
  // The fourth tab on the staff phone, and what sits behind it.
  More: { he: 'עוד', ur: 'مزید', tl: 'Higit pa' },
  'How this house likes things done': {
    he: 'איך נהוג לעשות דברים בבית הזה',
    ur: 'اس گھر میں کام کیسے ہوتے ہیں',
    tl: 'Kung paano ginagawa ang mga bagay dito',
  },
  'Give work and follow it': {
    he: 'לתת עבודה ולעקוב אחריה',
    ur: 'کام دیں اور اس کی پیروی کریں',
    tl: 'Magbigay ng gawain at subaybayan',
  },
  'Who works here': {
    he: 'מי עובד כאן',
    ur: 'یہاں کون کام کرتا ہے',
    tl: 'Sino ang nagtatrabaho dito',
  },
  'What the house spent': {
    he: 'מה הבית הוציא',
    ur: 'گھر نے کیا خرچ کیا',
    tl: 'Ang ginastos ng bahay',
  },
  'Your name, language and phone': {
    he: 'השם, השפה והטלפון שלך',
    ur: 'آپ کا نام، زبان اور فون',
    tl: 'Pangalan, wika at telepono mo',
  },
  // The four places the bottom bar was reduced to, and the round button.
  People: { he: 'אנשים', ur: 'لوگ', tl: 'Mga tao' },
  'Give work': { he: 'לתת משימה', ur: 'کام دیں', tl: 'Magbigay ng gawain' },
  'Pick a person and a day': {
    he: 'בוחרים אדם ויום',
    ur: 'ایک شخص اور دن چنیں',
    tl: 'Pumili ng tao at araw',
  },
  'Say it in your own words': {
    he: 'אומרים במילים שלך',
    ur: 'اپنے الفاظ میں کہیں',
    tl: 'Sabihin sa sarili mong salita',
  },
  'Work that repeats on its own': {
    he: 'עבודה שחוזרת מעצמה',
    ur: 'کام جو خود دہرایا جائے',
    tl: 'Gawaing paulit-ulit nang kusa',
  },
  // Notification titles. The server writes these in English — one short fixed
  // set — and the inbox translates them for whoever is reading.
  'Approval needed': { he: 'ממתין לאישור', ur: 'منظوری درکار', tl: 'Kailangan ng pag-apruba' },
  'Task started': { he: 'משימה התחילה', ur: 'کام شروع ہوا', tl: 'Nagsimula ang gawain' },
  'Task approved': { he: 'משימה אושרה', ur: 'کام منظور ہوا', tl: 'Naaprubahan ang gawain' },
  'Task rejected': { he: 'משימה נדחתה', ur: 'کام مسترد ہوا', tl: 'Tinanggihan ang gawain' },
  'Task cancelled': { he: 'משימה בוטלה', ur: 'کام منسوخ ہوا', tl: 'Kinansela ang gawain' },
  'Waiting on proof': { he: 'ממתין לתמונה', ur: 'ثبوت کا انتظار', tl: 'Naghihintay ng patunay' },
  'Cash request': { he: 'בקשת מזומן', ur: 'نقد کی درخواست', tl: 'Hiling na cash' },
  'Receipt uploaded': { he: 'קבלה הועלתה', ur: 'رسید اپ لوڈ ہوئی', tl: 'Na-upload ang resibo' },
  'Loan recorded': { he: 'הלוואה נרשמה', ur: 'قرض درج ہوا', tl: 'Naitala ang utang' },
  'Loan fully repaid': {
    he: 'ההלוואה נפרעה במלואה',
    ur: 'قرض مکمل ادا ہوا',
    tl: 'Bayad na ang utang',
  },
  'Loan written off': { he: 'ההלוואה נמחקה', ur: 'قرض معاف ہوا', tl: 'Kinansela ang utang' },
  'Payslip acknowledged': { he: 'התלוש אושר', ur: 'پے سلپ کی تصدیق', tl: 'Kinilala ang payslip' },
  'Your pay terms were updated': {
    he: 'תנאי השכר שלך עודכנו',
    ur: 'آپ کی تنخواہ کی شرائط بدل گئیں',
    tl: 'Na-update ang iyong pay terms',
  },
  'You received a bonus': {
    he: 'קיבלת בונוס',
    ur: 'آپ کو بونس ملا',
    tl: 'Nakatanggap ka ng bonus',
  },
  'A tip was recorded for you': {
    he: 'נרשם עבורך טיפ',
    ur: 'آپ کے لیے ٹپ درج ہوئی',
    tl: 'May naitalang tip para sa iyo',
  },
  'A deduction was recorded': {
    he: 'נרשם ניכוי',
    ur: 'کٹوتی درج ہوئی',
    tl: 'May naitalang bawas',
  },
  'A salary advance was recorded': {
    he: 'נרשמה מקדמה',
    ur: 'پیشگی تنخواہ درج ہوئی',
    tl: 'May naitalang advance',
  },
  'A payroll entry was recorded': {
    he: 'נרשמה רשומת שכר',
    ur: 'تنخواہ کا اندراج ہوا',
    tl: 'May naitalang payroll entry',
  },
  WhatsApp: { he: 'וואטסאפ', ur: 'واٹس ایپ', tl: 'WhatsApp' },
  'Your day arrives as a message, and what you reply comes back into the app.': {
    he: 'היום שלך מגיע כהודעה, ומה שאתה עונה חוזר לתוך האפליקציה.',
    ur: 'آپ کا دن ایک پیغام کی صورت آتا ہے، اور آپ کا جواب ایپ میں واپس آ جاتا ہے۔',
    tl: 'Dumarating ang araw mo bilang mensahe, at ang sagot mo ay babalik sa app.',
  },
  'Full number, starting with + and the country code.': {
    he: 'מספר מלא, שמתחיל ב‑+ ובקידומת המדינה.',
    ur: 'مکمل نمبر، + اور ملک کے کوڈ سے شروع۔',
    tl: 'Buong numero, nagsisimula sa + at country code.',
  },
  'Send me my tasks on WhatsApp': {
    he: 'שלחו לי את המשימות בוואטסאפ',
    ur: 'میرے کام واٹس ایپ پر بھیجیں',
    tl: 'Ipadala ang aking mga gawain sa WhatsApp',
  },
  'That does not look like a full number. Start with + and the country code.': {
    he: 'זה לא נראה כמו מספר מלא. תתחיל ב‑+ ובקידומת המדינה.',
    ur: 'یہ مکمل نمبر نہیں لگتا۔ + اور ملک کے کوڈ سے شروع کریں۔',
    tl: 'Mukhang hindi buong numero ito. Magsimula sa + at country code.',
  },
  // The week grid — routines laid out by person and day.
  // Urdu: "the week's plan", because bare ہفتہ also reads as Saturday.
  'The week': { he: 'השבוע', ur: 'ہفتے کا پلان', tl: 'Ang linggo' },
  'What this house does, and on which day. Written once — it repeats itself.': {
    he: 'מה נעשה בבית, ובאיזה יום. נכתב פעם אחת — וחוזר על עצמו.',
    ur: 'یہ گھر کیا کرتا ہے، اور کس دن۔ ایک بار لکھیں — پھر خود دہرائے گا۔',
    tl: 'Ang ginagawa sa bahay, at sa anong araw. Isang beses isulat — uulit na ito.',
  },
  'Add someone to the team first.': {
    he: 'קודם צריך להוסיף מישהו לצוות.',
    ur: 'پہلے ٹیم میں کسی کو شامل کریں۔',
    tl: 'Magdagdag muna ng tao sa team.',
  },
  'e.g. Wash the car': {
    he: 'למשל: לשטוף את האוטו',
    ur: 'مثلاً: گاڑی دھونا',
    tl: 'hal. Hugasan ang kotse',
  },
  'Everything here reaches the person on the day, and is marked late if nobody does it. Nobody has to ask.':
    {
      he: 'כל מה שכאן מגיע לאדם ביום עצמו, ומסומן כאיחור אם אף אחד לא עשה. אף אחד לא צריך לשאול.',
      ur: 'یہاں کی ہر بات اُسی دن متعلقہ شخص تک پہنچتی ہے، اور نہ ہونے پر تاخیر شمار ہوتی ہے۔ کسی کو پوچھنا نہیں پڑتا۔',
      tl: 'Lahat dito ay dumarating sa tao sa mismong araw, at mamarkahang late kung walang gumawa. Hindi na kailangang magtanong.',
    },
  Sun: { he: 'א׳', ur: 'اتوار', tl: 'Lin' },
  Mon: { he: 'ב׳', ur: 'پیر', tl: 'Lun' },
  Tue: { he: 'ג׳', ur: 'منگل', tl: 'Mar' },
  Wed: { he: 'ד׳', ur: 'بدھ', tl: 'Miy' },
  Thu: { he: 'ה׳', ur: 'جمعرات', tl: 'Huw' },
  Fri: { he: 'ו׳', ur: 'جمعہ', tl: 'Biy' },
  Sat: { he: 'ש׳', ur: 'ہفتہ', tl: 'Sab' },
  Saved: { he: 'נשמר', ur: 'محفوظ', tl: 'Naka-save' },
  'Plan a task in your own words…': {
    he: 'תכנן משימה במילים שלך…',
    ur: 'اپنے الفاظ میں کام بتائیں…',
    tl: 'Mag-plano ng task sa sariling salita…',
  },
  Parse: { he: 'נתח', ur: 'تجزیہ', tl: 'I-parse' },
  Create: { he: 'צור', ur: 'بنائیں', tl: 'Lumikha' },
  'Speak instead': { he: 'דבר במקום', ur: 'بولیں', tl: 'Magsalita' },
  'Listening…': { he: 'מקשיב…', ur: 'سن رہا ہے…', tl: 'Nakikinig…' },
  'Drag to reorder · tap to edit': {
    he: 'גרור לסידור · הקש לעריכה',
    ur: 'ترتیب بدلیں · ترمیم کریں',
    tl: 'Pindutin para mag-edit',
  },
  Title: { he: 'כותרת', ur: 'عنوان', tl: 'Pamagat' },
  Description: { he: 'תיאור', ur: 'تفصیل', tl: 'Paglalarawan' },
  'When?': { he: 'מתי?', ur: 'کب؟', tl: 'Kailan?' },
  'Now (send)': { he: 'עכשיו (שלח)', ur: 'ابھی (بھیجیں)', tl: 'Ngayon (ipadala)' },
  Later: { he: 'מאוחר יותר', ur: 'بعد میں', tl: 'Mamaya' },
  Confidence: { he: 'ביטחון', ur: 'اعتماد', tl: 'Tiwala' },
  'Recognized intent': { he: 'כוונה שזוהתה', ur: 'پہچانی گئی نیت', tl: 'Natukoy na layunin' },
  'Create task': { he: 'צור משימה', ur: 'کام بنائیں', tl: 'Gumawa ng task' },
  'e.g. Pick the kids up at 16:00': {
    he: 'לדוגמה: לאסוף את הילדים ב־16:00',
    ur: 'مثال: 16:00 پر بچوں کو لیں',
    tl: 'hal. Sunduin ang mga bata ng 16:00',
  },
  'e.g. 24 Allenby St, Tel Aviv': {
    he: 'לדוגמה: אלנבי 24, תל אביב',
    ur: 'مثال: النبی اسٹریٹ 24، تل ابیب',
    tl: 'hal. 24 Allenby St, Tel Aviv',
  },
  'Send now': { he: 'שלח עכשיו', ur: 'ابھی بھیجیں', tl: 'Ipadala ngayon' },
  'Require proof when done': {
    he: 'דרוש הוכחה בסיום',
    ur: 'مکمل ہونے پر ثبوت درکار',
    tl: 'Kailangan ng patunay kapag tapos',
  },
  Photo: { he: 'תמונה', ur: 'تصویر', tl: 'Larawan' },
  Voice: { he: 'הקלטה', ur: 'آواز', tl: 'Boses' },
  Text: { he: 'טקסט', ur: 'متن', tl: 'Teksto' },
  'Add a photo or voice note from the task detail screen after creating.': {
    he: 'אפשר לצרף תמונה או הקלטה ממסך פרטי המשימה אחרי היצירה.',
    ur: 'تخلیق کے بعد ٹاسک کی تفصیل سے تصویر یا وائس نوٹ شامل کریں۔',
    tl: 'Magdagdag ng larawan o voice note mula sa task detail pagkatapos lumikha.',
  },
  Attachments: { he: 'קבצים מצורפים', ur: 'منسلکات', tl: 'Mga kalakip' },
  File: { he: 'קובץ', ur: 'فائل', tl: 'File' },
  'Speak as you would in real life. The AI figures out who, what, when, and proof.': {
    he: 'דבר כמו שהיית אומר. ה-AI יבין מי, מה, מתי, האם דרושה הוכחה.',
    ur: 'جیسے بات کرتے ہیں ویسے لکھیں۔ AI خود سمجھے گا کون، کیا، کب۔',
    tl: 'Magsulat tulad ng natural na salita. Hahanapin ng AI kung sino, ano, kailan.',
  },
  'No routines yet.': {
    he: 'אין עדיין שגרות.',
    ur: 'ابھی کوئی روٹین نہیں۔',
    tl: 'Wala pang rutina.',
  },
  Next: { he: 'הבא', ur: 'اگلا', tl: 'Susunod' },
  Paused: { he: 'מושהה', ur: 'روک دیا', tl: 'Naka-pause' },
  Resume: { he: 'המשך', ur: 'دوبارہ شروع', tl: 'Magpatuloy' },
  Pause: { he: 'השהה', ur: 'روک دیں', tl: 'I-pause' },
  'Skip next': { he: 'דלג על הבא', ur: 'اگلا چھوڑیں', tl: 'Laktawan susunod' },
  'New routine': { he: 'שגרה חדשה', ur: 'نئی روٹین', tl: 'Bagong rutina' },
  Daily: { he: 'יומי', ur: 'روزانہ', tl: 'Araw-araw' },
  'Weekdays (Sun–Thu)': {
    he: 'ימי חול (א-ה)',
    ur: 'ہفتے کے دن (اتوار-جمعرات)',
    tl: 'Linggo-Huwebes',
  },
  'Weekend (Fri–Sat)': { he: 'סוף שבוע (ו-ש)', ur: 'ویک اینڈ', tl: 'Sabado-Linggo' },
  'Once a week': { he: 'פעם בשבוע', ur: 'ہفتے میں ایک بار', tl: 'Isang beses sa linggo' },
  'Custom (RRULE)': { he: 'מותאם אישית (RRULE)', ur: 'حسب ضرورت (RRULE)', tl: 'Custom (RRULE)' },

  // Onboarding
  'Create your family': {
    he: 'צור את המשפחה שלך',
    ur: 'اپنا خاندان بنائیں',
    tl: 'Likhain ang iyong pamilya',
  },
  'e.g. The Cohen family': {
    he: 'לדוגמה: משפחת כהן',
    ur: 'مثال: کوہن خاندان',
    tl: 'hal. Pamilyang Cohen',
  },
  'How many family members?': {
    he: 'כמה בני משפחה?',
    ur: 'خاندان میں کتنے افراد ہیں؟',
    tl: 'Ilang miyembro ng pamilya?',
  },
  'Just me': { he: 'רק אני', ur: 'صرف میں', tl: 'Ako lang' },
  'Do you have pets?': {
    he: 'יש לך חיות מחמד?',
    ur: 'کیا آپ کے پاس پالتو جانور ہیں؟',
    tl: 'May alagang hayop ka ba?',
  },
  No: { he: 'לא', ur: 'نہیں', tl: 'Wala' },
  Dog: { he: 'כלב', ur: 'کتا', tl: 'Aso' },
  Cat: { he: 'חתול', ur: 'بلی', tl: 'Pusa' },
  Multiple: { he: 'כמה', ur: 'متعدد', tl: 'Marami' },
  Other: { he: 'אחר', ur: 'دیگر', tl: 'Iba' },
  'How many household staff?': {
    he: 'כמה עובדים בבית?',
    ur: 'گھر میں کتنے ملازم ہیں؟',
    tl: 'Ilang tauhan sa bahay?',
  },
  'Create family': { he: 'צור משפחה', ur: 'خاندان بنائیں', tl: 'Likhain ang pamilya' },

  // Quickstart
  Quickstart: { he: 'התחלה מהירה', ur: 'فوری شروعات', tl: 'Mabilis na simula' },
  'Add your first staff member': {
    he: 'הוסף עובד ראשון',
    ur: 'پہلا عملہ شامل کریں',
    tl: 'Magdagdag ng unang tauhan',
  },
  'Create first household task': {
    he: 'צור משימה ראשונה',
    ur: 'پہلا گھریلو کام بنائیں',
    tl: 'Gumawa ng unang gawain',
  },
  'Add a routine': { he: 'הוסף שגרה', ur: 'روٹین شامل کریں', tl: 'Magdagdag ng rutina' },
  'Optional context for the team': {
    he: 'הקשר לעובדים (לא חובה)',
    ur: 'ٹیم کے لیے اضافی تفصیل (اختیاری)',
    tl: 'Karagdagang detalye para sa team',
  },
  'Staff will see this task as cancelled. Add a reason if you like.': {
    he: 'העובד יראה שהמשימה בוטלה. אפשר להוסיף סיבה.',
    ur: 'عملہ دیکھے گا کہ کام منسوخ ہو گیا۔ وجہ شامل کر سکتے ہیں۔',
    tl: 'Makikita ng tauhan na kinansela ang gawain. Magdagdag ng dahilan kung gusto mo.',
  },
  'The task disappears from the dashboard. History stays intact.': {
    he: 'המשימה תיעלם מהדשבורד. ההיסטוריה נשמרת.',
    ur: 'کام ڈیش بورڈ سے ہٹ جائے گا۔ تاریخ محفوظ رہے گی۔',
    tl: 'Mawawala ang task sa dashboard. Mananatili ang kasaysayan.',
  },

  // Location / maps
  Address: { he: 'כתובת', ur: 'پتہ', tl: 'Address' },
  'Address (optional)': {
    he: 'כתובת (לא חובה)',
    ur: 'پتہ (اختیاری)',
    tl: 'Address (opsyonal)',
  },
  'Open in Google Maps': {
    he: 'פתח ב־Google Maps',
    ur: 'گوگل میپس میں کھولیں',
    tl: 'Buksan sa Google Maps',
  },
  'Open in Waze': { he: 'פתח ב־Waze', ur: 'ویز میں کھولیں', tl: 'Buksan sa Waze' },
  'Open in Apple Maps': {
    he: 'פתח ב־Apple Maps',
    ur: 'ایپل میپس میں کھولیں',
    tl: 'Buksan sa Apple Maps',
  },
  Navigate: { he: 'ניווט', ur: 'نیویگیٹ', tl: 'Mag-navigate' },
  'Open in…': { he: 'פתח ב…', ur: 'کھولیں…', tl: 'Buksan sa…' },
  'Default navigation app': {
    he: 'אפליקציית ניווט ברירת מחדל',
    ur: 'پہلے سے طے شدہ نیویگیشن ایپ',
    tl: 'Default na nabigasyon app',
  },
  'Used when you tap Navigate on a task.': {
    he: 'בשימוש כשמקישים על "ניווט" במשימה.',
    ur: 'جب آپ کسی کام پر "نیویگیٹ" پر ٹیپ کریں گے تو استعمال ہوگی۔',
    tl: 'Ginagamit kapag pinindot mo ang Navigate sa isang gawain.',
  },
  'Google Maps': { he: 'Google Maps', ur: 'گوگل میپس', tl: 'Google Maps' },
  Waze: { he: 'Waze', ur: 'ویز', tl: 'Waze' },
  'Apple Maps': { he: 'Apple Maps', ur: 'ایپل میپس', tl: 'Apple Maps' },

  // Co-owner invites
  'Invite a co-owner': {
    he: 'הזמן בעלים שותף',
    ur: 'شریک مالک کو مدعو کریں',
    tl: 'Mag-imbita ng katuwang na may-ari',
  },
  'Invite your spouse or partner so they can assign tasks too.': {
    he: 'הזמן את בן/בת הזוג שיוכלו גם להקצות משימות.',
    ur: 'اپنے ساتھی کو مدعو کریں تاکہ وہ بھی کام تفویض کر سکیں۔',
    tl: 'Imbitahan ang asawa o kapareha mo para makapag-assign din.',
  },
  'Generate invite link': {
    he: 'צור קישור הזמנה',
    ur: 'دعوتی لنک بنائیں',
    tl: 'Gumawa ng link ng paanyaya',
  },
  'Copy link': { he: 'העתק קישור', ur: 'لنک کاپی کریں', tl: 'Kopyahin ang link' },
  'Link copied': { he: 'הועתק', ur: 'کاپی ہو گیا', tl: 'Nakopya' },
  'Share via WhatsApp': {
    he: 'שלח ב־WhatsApp',
    ur: 'واٹس ایپ پر بھیجیں',
    tl: 'Ipadala sa WhatsApp',
  },
  'Pending invites': { he: 'הזמנות פתוחות', ur: 'زیر التواء دعوتیں', tl: 'Mga paanyaya' },
  Revoke: { he: 'בטל', ur: 'منسوخ', tl: 'Bawiin' },
  Expires: { he: 'תוקף עד', ur: 'ختم ہوگی', tl: 'Mawawalan ng bisa' },
  "You've been invited to join {{name}}": {
    he: 'הוזמנת להצטרף ל{{name}}',
    ur: '{{name}} میں شامل ہونے کی دعوت',
    tl: 'Inimbita ka sa {{name}}',
  },
  'Invited by {{name}}': {
    he: 'הוזמנת על־ידי {{name}}',
    ur: '{{name}} نے مدعو کیا',
    tl: 'Inimbita ni {{name}}',
  },
  'Accept invite': { he: 'אישור הצטרפות', ur: 'دعوت قبول کریں', tl: 'Tanggapin ang paanyaya' },

  // Notifications (browser web push)
  Notifications: { he: 'התראות', ur: 'اطلاعات', tl: 'Mga abiso' },
  'Get a browser banner when tasks need your attention.': {
    he: 'קבל באנר בדפדפן כשמשימה דורשת תשומת לב.',
    ur: 'جب کام پر توجہ درکار ہو تو براؤزر بینر حاصل کریں۔',
    tl: 'Tumanggap ng browser banner kapag may gawaing kailangan.',
  },
  'Enable notifications': {
    he: 'הפעל התראות',
    ur: 'اطلاعات فعال کریں',
    tl: 'I-enable ang mga abiso',
  },
  'Notifications enabled': {
    he: 'התראות פעילות',
    ur: 'اطلاعات فعال',
    tl: 'Aktibo ang mga abiso',
  },
  Disable: { he: 'בטל', ur: 'غیر فعال کریں', tl: 'I-disable' },
  'This browser does not support push notifications.': {
    he: 'הדפדפן לא תומך בהתראות דחיפה.',
    ur: 'یہ براؤزر پش اطلاعات کی حمایت نہیں کرتا۔',
    tl: 'Hindi sinusuportahan ng browser na ito ang push notifications.',
  },
  'Notifications are blocked. Allow them in your browser settings.': {
    he: 'התראות חסומות. אפשר אותן בהגדרות הדפדפן.',
    ur: 'اطلاعات بلاک ہیں۔ براؤزر کی ترتیبات میں اجازت دیں۔',
    tl: 'Naka-block ang notifications. Payagan ito sa browser settings.',
  },

  // Staff invites (shareable link, WhatsApp-friendly)
  'Invite staff member': {
    he: 'הזמן עובד',
    ur: 'عملہ کو مدعو کریں',
    tl: 'Mag-imbita ng tauhan',
  },
  'Invite via link': {
    he: 'הזמן עם קישור',
    ur: 'لنک سے مدعو کریں',
    tl: 'Mag-imbita gamit ang link',
  },
  'Send invite link': {
    he: 'שלח קישור הזמנה',
    ur: 'دعوتی لنک بھیجیں',
    tl: 'Magpadala ng link ng paanyaya',
  },
  'Generate a one-time link so this staff member can set their own password.': {
    he: 'צור קישור חד-פעמי שיאפשר לעובד להגדיר סיסמה משלו.',
    ur: 'ایک بار کا لنک بنائیں تاکہ یہ عملہ اپنا پاس ورڈ خود طے کر سکے۔',
    tl: 'Gumawa ng isang beses na link para makapag-set ng sariling password ang tauhang ito.',
  },
  'You have been invited to join the household. Set up your account here:': {
    he: 'הוזמנת להצטרף למשק הבית. הגדר את חשבונך כאן:',
    ur: 'آپ کو گھرانے میں شامل ہونے کی دعوت ہے۔ اپنا اکاؤنٹ یہاں ترتیب دیں:',
    tl: 'Inimbita ka sa sambahayan. I-set up ang account mo dito:',
  },
  'Welcome, {{name}}': {
    he: 'ברוך הבא, {{name}}',
    ur: 'خوش آمدید، {{name}}',
    tl: 'Maligayang pagdating, {{name}}',
  },
  "You're joining {{name}} as staff": {
    he: 'אתה מצטרף ל{{name}} כעובד',
    ur: 'آپ {{name}} میں عملہ کے طور پر شامل ہو رہے ہیں',
    tl: 'Sasali ka sa {{name}} bilang tauhan',
  },
  'Choose a password': {
    he: 'בחר סיסמה',
    ur: 'پاس ورڈ منتخب کریں',
    tl: 'Pumili ng password',
  },

  // Staff first-time tour (3-screen overlay shown once on first /today visit).
  // Tone: spoken to a Filipina housekeeper who has never used a task app.
  // Plain, gender-neutral plural in Hebrew. No emojis (brand rule).
  "Here's how famanage works for you.": {
    he: 'ככה famanage עובד בשבילך.',
    ur: 'famanage آپ کے لیے ایسے کام کرتا ہے۔',
    tl: 'Ganito gumagana ang famanage para sa iyo.',
  },
  'Tap a task when you start it': {
    he: 'הקש על משימה כשמתחילים אותה',
    ur: 'جب کام شروع کریں تو اس پر ٹیپ کریں',
    tl: 'Pindutin ang gawain kapag sisimulan mo',
  },
  'When you\'re working on something, tap "I\'m starting". Your owner sees instantly.': {
    he: 'כשעובדים על משהו, להקיש על "מתחיל". המעסיק רואה מיד.',
    ur: 'جب کوئی کام کر رہے ہوں تو "شروع کر رہا ہوں" پر ٹیپ کریں۔ مالک کو فوراً نظر آئے گا۔',
    tl: 'Kapag may ginagawa ka, pindutin ang "Sisimulan ko". Agad na makikita ng may-ari.',
  },
  "Send proof when you're done": {
    he: 'שולחים הוכחה כשמסיימים',
    ur: 'مکمل ہونے پر ثبوت بھیجیں',
    tl: 'Magpadala ng patunay kapag tapos na',
  },
  'Take a photo or record a voice note when you finish. Your owner approves it.': {
    he: 'מצלמים תמונה או מקליטים הודעה קולית בסיום. המעסיק מאשר.',
    ur: 'مکمل کرنے پر تصویر لیں یا وائس نوٹ ریکارڈ کریں۔ مالک منظوری دے گا۔',
    tl: 'Kumuha ng larawan o mag-record ng voice note kapag tapos ka na. Aaprubahan ng may-ari mo.',
  },
  'Got it': { he: 'הבנתי', ur: 'سمجھ گیا', tl: 'Nakuha ko' },
  'Skip tour': { he: 'דלג על הסבר', ur: 'ٹور چھوڑیں', tl: 'Laktawan ang tour' },

  // Tasks list page
  Completed: { he: 'הושלמו', ur: 'مکمل', tl: 'Tapos' },
  All: { he: 'הכל', ur: 'سب', tl: 'Lahat' },
  'No tasks here.': { he: 'אין משימות כאן.', ur: 'یہاں کوئی کام نہیں۔', tl: 'Walang gawain dito.' },
  'Search title, description, address…': {
    he: 'חיפוש כותרת, תיאור, כתובת…',
    ur: 'عنوان، تفصیل، پتہ تلاش کریں…',
    tl: 'Hanapin sa pamagat, paglalarawan, address…',
  },
  'All staff': { he: 'כל הצוות', ur: 'تمام عملہ', tl: 'Lahat ng tauhan' },
  task: { he: 'משימה', ur: 'کام', tl: 'gawain' },
  tasks: { he: 'משימות', ur: 'کام', tl: 'mga gawain' },

  // Magic moment: translation badge after task create
  'Sent to {{name}}': {
    he: 'נשלח ל{{name}}',
    ur: '{{name}} کو بھیجا',
    tl: 'Ipinadala kay {{name}}',
  },
  '{{name}} will see this in {{lang}}': {
    he: '{{name}} יראה את זה ב{{lang}}',
    ur: '{{name}} اسے {{lang}} میں دیکھے گا',
    tl: 'Makikita ito ni {{name}} sa {{lang}}',
  },
  'Translating into {{lang}}…': {
    he: 'מתרגם ל{{lang}}…',
    ur: '{{lang}} میں ترجمہ ہو رہا ہے…',
    tl: 'Sinasalin sa {{lang}}…',
  },
  'View task': { he: 'פתח משימה', ur: 'کام دیکھیں', tl: 'Tingnan ang gawain' },
  'Create another': { he: 'צור משימה נוספת', ur: 'دوسرا بنائیں', tl: 'Gumawa ulit' },

  // Receipt-scan loop (staff scans a shop receipt → AI proposes a task/routine)
  'Scan a receipt': {
    he: 'סרוק קבלה',
    ur: 'رسید اسکین کریں',
    tl: 'Mag-scan ng resibo',
  },
  'Snap a photo · we propose a task or routine for your owner.': {
    he: 'צלם תמונה · נציע משימה או שגרה למעסיק שלך.',
    ur: 'تصویر لیں · ہم آپ کے مالک کے لیے کام یا روٹین تجویز کریں گے۔',
    tl: 'Kumuha ng larawan · magmumungkahi kami ng task o rutina sa may-ari mo.',
  },
  'Open camera': { he: 'פתח מצלמה', ur: 'کیمرہ کھولیں', tl: 'Buksan ang kamera' },
  'Take photo': { he: 'צלם תמונה', ur: 'تصویر لیں', tl: 'Kumuha ng larawan' },
  'Choose from gallery': {
    he: 'בחר מהגלריה',
    ur: 'گیلری سے منتخب کریں',
    tl: 'Pumili mula sa gallery',
  },
  'Type it out instead': {
    he: 'הקלד במקום',
    ur: 'اس کے بجائے ٹائپ کریں',
    tl: 'I-type na lang',
  },
  'Describe what you bought': {
    he: 'תאר מה קנית',
    ur: 'بتائیں آپ نے کیا خریدا',
    tl: 'Ilarawan ang binili mo',
  },
  'e.g. 3 bags of chlorine and a pool brush from Carrefour, 657 AED': {
    he: 'לדוגמא 3 שקיות כלור ומברשת לבריכה מקרפור, 657 AED',
    ur: 'مثلاً کارفور سے 3 تھیلے کلورین اور پول برش، 657 AED',
    tl: 'hal. 3 sako ng chlorine at pool brush sa Carrefour, 657 AED',
  },
  Submit: { he: 'שלח', ur: 'جمع کریں', tl: 'Isumite' },
  'Take a photo, pick from gallery, or type it out.': {
    he: 'צלם תמונה, בחר מהגלריה, או הקלד את זה.',
    ur: 'تصویر لیں، گیلری سے منتخب کریں، یا ٹائپ کریں۔',
    tl: 'Kumuha ng larawan, pumili sa gallery, o i-type.',
  },
  'We will read the items, ask one question, and propose a task or routine for your owner to approve.':
    {
      he: 'נקרא את הפריטים, נשאל שאלה אחת, ונציע משימה או שגרה לאישור המעסיק.',
      ur: 'ہم اشیاء پڑھیں گے، ایک سوال پوچھیں گے، اور آپ کے مالک کی منظوری کے لیے کام یا روٹین تجویز کریں گے۔',
      tl: 'Babasahin namin ang mga item, isang tanong lang, at imumungkahi sa may-ari ang task o rutina.',
    },
  'Type your answer or use voice…': {
    he: 'הקלד תשובה או דבר…',
    ur: 'جواب لکھیں یا آواز استعمال کریں…',
    tl: 'I-type ang sagot mo o magsalita…',
  },
  'Transcribing…': { he: 'ממיר לטקסט…', ur: 'متن میں بدل رہا ہے…', tl: 'Iniipatutugma…' },
  'Could not read this receipt': {
    he: 'לא הצלחנו לקרוא את הקבלה הזו',
    ur: 'یہ رسید پڑھنے میں ناکام',
    tl: 'Hindi mabasa ang resibong ito',
  },
  'Try a clearer photo or a different angle.': {
    he: 'נסה תמונה ברורה יותר או זווית אחרת.',
    ur: 'صاف تصویر یا مختلف زاویہ سے کوشش کریں۔',
    tl: 'Subukan ang mas malinaw na larawan o ibang anggulo.',
  },
  'Try again': { he: 'נסה שוב', ur: 'دوبارہ کوشش', tl: 'Subukan muli' },
  'Retry photo': { he: 'צלם מחדש', ur: 'دوبارہ تصویر لیں', tl: 'Mag-photo muli' },
  'Ready to send to owner': {
    he: 'מוכן לשליחה למעסיק',
    ur: 'مالک کو بھیجنے کے لیے تیار',
    tl: 'Handa nang ipadala sa may-ari',
  },
  'Send to owner for approval': {
    he: 'שלח למעסיק לאישור',
    ur: 'منظوری کے لیے مالک کو بھیجیں',
    tl: 'Ipadala sa may-ari para sa pag-apruba',
  },
  'Proposed task': { he: 'משימה מוצעת', ur: 'تجویز کردہ کام', tl: 'Mungkahing gawain' },
  'Proposed routine': { he: 'שגרה מוצעת', ur: 'تجویز کردہ روٹین', tl: 'Mungkahing rutina' },
  // Two-phase receipt flow: OCR → confirm → analyze.
  'Reading…': { he: 'קורא…', ur: 'پڑھ رہا ہے…', tl: 'Binabasa…' },
  'We will read the receipt and show what we got.': {
    he: 'נקרא את הקבלה ונציג מה קלטנו.',
    ur: 'ہم رسید پڑھیں گے اور دکھائیں گے۔',
    tl: 'Babasahin namin ang resibo at ipapakita.',
  },
  'Here is what I read': {
    he: 'הנה מה שקראתי',
    ur: 'یہ ہے جو میں نے پڑھا',
    tl: 'Ito ang nabasa ko',
  },
  'Confirm reading': {
    he: 'אישור הקריאה',
    ur: 'پڑھنے کی تصدیق کریں',
    tl: 'Kumpirmahin ang nabasa',
  },
  'Retake photo': {
    he: 'צלם מחדש',
    ur: 'دوبارہ تصویر لیں',
    tl: 'Kumuha muli ng larawan',
  },
  'Thinking…': { he: 'חושב…', ur: 'سوچ رہا ہے…', tl: 'Nag-iisip…' },
  'No line items extracted.': {
    he: 'לא חולצו פריטים.',
    ur: 'کوئی آئٹم نہیں ملا۔',
    tl: 'Walang nakuhang item.',
  },
  'Sent to your owner': {
    he: 'נשלח למעסיק',
    ur: 'مالک کو بھیج دیا',
    tl: 'Ipinadala sa may-ari mo',
  },
  'They will see the proposal and approve or reject it.': {
    he: 'הם יראו את ההצעה ויאשרו או ידחו.',
    ur: 'وہ تجویز دیکھیں گے اور منظوری دیں گے یا رد کریں گے۔',
    tl: 'Makikita nila ang mungkahi at aaprubahan o tatanggihan.',
  },
  Dismiss: { he: 'הסר', ur: 'بند', tl: 'I-dismiss' },
  'Receipt proposals': {
    he: 'הצעות מקבלות',
    ur: 'رسید کی تجاویز',
    tl: 'Mga mungkahi mula sa resibo',
  },
  Task: { he: 'משימה', ur: 'کام', tl: 'Gawain' },
  Routine: { he: 'שגרה', ur: 'روٹین', tl: 'Rutina' },
  '(empty proposal)': {
    he: '(הצעה ריקה)',
    ur: '(خالی تجویز)',
    tl: '(walang laman)',
  },

  // Monthly expenses dashboard (Money screen + Owner home tile)
  'This month': { he: 'החודש', ur: 'اس مہینے', tl: 'Ngayong buwan' },
  'across {{n}} receipts': {
    he: 'מעל {{n}} קבלות',
    ur: '{{n}} رسیدوں پر',
    tl: 'sa {{n}} resibo',
  },
  'No spend tracked yet': {
    he: 'עדיין לא נרשמו הוצאות',
    ur: 'ابھی تک کوئی خرچ ریکارڈ نہیں',
    tl: 'Wala pang naitalang gastos',
  },
  'Previous month': {
    he: 'חודש קודם',
    ur: 'پچھلا مہینہ',
    tl: 'Nakaraang buwan',
  },
  'Next month': { he: 'חודש הבא', ur: 'اگلا مہینہ', tl: 'Susunod na buwan' },
  'Spent in {{month}}': {
    he: 'הוצאה ב{{month}}',
    ur: '{{month}} میں خرچ',
    tl: 'Gastos sa {{month}}',
  },
  'By category': {
    he: 'לפי קטגוריה',
    ur: 'زمرے کے لحاظ سے',
    tl: 'Ayon sa kategorya',
  },
  'By staff': {
    he: 'לפי עובד',
    ur: 'عملہ کے لحاظ سے',
    tl: 'Ayon sa tauhan',
  },
  'Recent receipts': {
    he: 'קבלות אחרונות',
    ur: 'حالیہ رسیدیں',
    tl: 'Mga huling resibo',
  },
  'No receipts in this month yet.': {
    he: 'אין קבלות בחודש הזה.',
    ur: 'اس مہینے کوئی رسید نہیں۔',
    tl: 'Walang resibo sa buwang ito.',
  },
  'Multiple currencies detected — showing {{currency}} only.': {
    he: 'התגלו כמה מטבעות — מציג רק {{currency}}.',
    ur: 'متعدد کرنسیاں — صرف {{currency}} دکھائی جا رہی ہیں۔',
    tl: 'Maraming currency — {{currency}} lang ang ipinapakita.',
  },
  '{{count}} receipts': {
    he: '{{count}} קבלות',
    ur: '{{count}} رسیدیں',
    tl: '{{count}} resibo',
  },
  Pool: { he: 'בריכה', ur: 'تالاب', tl: 'Pool' },
  Cleaning: { he: 'ניקיון', ur: 'صفائی', tl: 'Paglilinis' },
  Pet: { he: 'חיית מחמד', ur: 'پالتو جانور', tl: 'Alagang hayop' },
  Groceries: { he: 'מצרכים', ur: 'گروسری', tl: 'Pamimili' },
  Baby: { he: 'תינוק', ur: 'بچہ', tl: 'Sanggol' },
  Pharmacy: { he: 'בית מרקחת', ur: 'فارمیسی', tl: 'Botika' },
  Fuel: { he: 'דלק', ur: 'ایندھن', tl: 'Gasolina' },
  Reimbursements: {
    he: 'החזרים',
    ur: 'رقم کی واپسی',
    tl: 'Mga reimbursement',
  },
  'No reimbursements yet.': {
    he: 'אין החזרים כרגע.',
    ur: 'ابھی کوئی واپسی نہیں۔',
    tl: 'Wala pang reimbursement.',
  },
  Submitted: { he: 'הוגש', ur: 'جمع کرایا', tl: 'Ipinasa' },
  Paid: { he: 'שולם', ur: 'ادا کیا', tl: 'Nabayaran' },
  'Mark paid': { he: 'סמן ששולם', ur: 'ادا کیا گیا نشان زد کریں', tl: 'I-mark na bayad' },
  'Cash flow': { he: 'תזרים כסף', ur: 'نقد بہاؤ', tl: 'Daloy ng pera' },
  'Cash given': { he: 'מזומן שניתן', ur: 'دی گئی نقدی', tl: 'Cash na ibinigay' },
  'No cash dispensed yet.': {
    he: 'לא ניתן מזומן עדיין.',
    ur: 'ابھی تک کوئی نقدی نہیں دی گئی۔',
    tl: 'Wala pang cash na ibinigay.',
  },
  Dispensed: { he: 'ניתן', ur: 'دی گئی', tl: 'Ibinigay' },
  Cash: { he: 'מזומן', ur: 'نقد', tl: 'Cash' },
  Card: { he: 'כרטיס', ur: 'کارڈ', tl: 'Card' },
  'Payment method?': {
    he: 'דרך תשלום?',
    ur: 'ادائیگی کا طریقہ؟',
    tl: 'Paraan ng bayad?',
  },
  Receipt: { he: 'קבלה', ur: 'رسید', tl: 'Resibo' },
  Items: { he: 'פריטים', ur: 'اشیاء', tl: 'Mga item' },

  // Magic moment: WhatsApp-style read receipts
  Read: { he: 'נקרא', ur: 'پڑھ لیا', tl: 'Nabasa' },
  Delivered: { he: 'נמסר', ur: 'پہنچا', tl: 'Naipadala' },
  'First seen by {{name}} at {{time}}': {
    he: 'נראה לראשונה על־ידי {{name}} ב־{{time}}',
    ur: 'پہلی بار {{name}} نے {{time}} پر دیکھا',
    tl: 'Unang nakita ni {{name}} sa {{time}}',
  },

  // === Payroll ===
  // Deliberately plain words in every language: this screen decides what a
  // person takes home, and nobody should have to guess what a label means.
  Payroll: { he: 'שכר', ur: 'تنخواہ', tl: 'Sahod' },
  Settings: { he: 'הגדרות', ur: 'ترتیبات', tl: 'Mga setting' },
  'Payroll settings': { he: 'הגדרות שכר', ur: 'تنخواہ کی ترتیبات', tl: 'Mga setting ng sahod' },
  'Not run yet': { he: 'טרם הופעל', ur: 'ابھی نہیں چلایا', tl: 'Hindi pa pinatakbo' },
  Finalized: { he: 'סופי', ur: 'حتمی', tl: 'Pinal' },
  Ready: { he: 'מוכן', ur: 'تیار', tl: 'Handa na' },
  'Generate payroll': { he: 'הפק שכר', ur: 'تنخواہ بنائیں', tl: 'Gumawa ng sahod' },
  Recalculate: { he: 'חשב מחדש', ur: 'دوبارہ حساب', tl: 'Kalkulahin muli' },
  Finalize: { he: 'סגור סופית', ur: 'حتمی کریں', tl: 'Tapusin' },
  'Discard draft': { he: 'מחק טיוטה', ur: 'مسودہ ضائع کریں', tl: 'Itapon ang burador' },
  'Salaries paid for this month.': {
    he: 'המשכורות שולמו החודש.',
    ur: 'اس مہینے کی تنخواہیں ادا ہو گئیں۔',
    tl: 'Nabayaran na ang sahod ngayong buwan.',
  },
  'Pay terms are missing': {
    he: 'חסרים תנאי שכר',
    ur: 'تنخواہ کی شرائط موجود نہیں',
    tl: 'Walang kasunduan sa sahod',
  },
  'These people are not on payroll yet — set their salary to include them.': {
    he: 'האנשים האלה עדיין לא בשכר — קבע להם משכורת כדי לכלול אותם.',
    ur: 'یہ لوگ ابھی تنخواہ میں شامل نہیں — انہیں شامل کرنے کے لیے تنخواہ مقرر کریں۔',
    tl: 'Wala pa sila sa payroll — itakda ang sahod para maisama sila.',
  },
  'Set pay for {{name}}': {
    he: 'קבע שכר ל־{{name}}',
    ur: '{{name}} کی تنخواہ مقرر کریں',
    tl: 'Itakda ang sahod ni {{name}}',
  },
  'Take-home total for {{month}}': {
    he: 'סה״כ נטו ל־{{month}}',
    ur: '{{month}} کے لیے کل خالص',
    tl: 'Kabuuang uuwiin para sa {{month}}',
  },
  '{{count}} people': { he: '{{count}} אנשים', ur: '{{count}} افراد', tl: '{{count}} tao' },
  'gross {{amount}}': { he: 'ברוטו {{amount}}', ur: 'مجموعی {{amount}}', tl: 'gross {{amount}}' },
  '{{amount}} goes to loan repayment': {
    he: '{{amount}} הולך להחזר הלוואה',
    ur: '{{amount}} قرض کی واپسی میں جاتا ہے',
    tl: '{{amount}} ang napupunta sa bayad-utang',
  },
  'This month, person by person': {
    he: 'החודש, אדם אחרי אדם',
    ur: 'اس مہینے، ہر فرد الگ الگ',
    tl: 'Ngayong buwan, isa-isa',
  },
  'Nobody is on payroll yet. Set someone’s pay terms to start.': {
    he: 'אף אחד עדיין לא בשכר. קבע תנאי שכר למישהו כדי להתחיל.',
    ur: 'ابھی کوئی تنخواہ میں نہیں۔ شروع کرنے کے لیے کسی کی تنخواہ مقرر کریں۔',
    tl: 'Wala pang nasa payroll. Magtakda ng sahod para magsimula.',
  },
  'per month': { he: 'לחודש', ur: 'ماہانہ', tl: 'kada buwan' },
  'per day': { he: 'ליום', ur: 'روزانہ', tl: 'kada araw' },
  'per hour': { he: 'לשעה', ur: 'فی گھنٹہ', tl: 'kada oras' },
  'overtime ×{{x}}': {
    he: 'שעות נוספות ×{{x}}',
    ur: 'اوور ٹائم ×{{x}}',
    tl: 'overtime ×{{x}}',
  },
  'take-home': { he: 'נטו', ur: 'خالص', tl: 'uuwiin' },
  'Show breakdown': { he: 'הצג פירוט', ur: 'تفصیل دکھائیں', tl: 'Ipakita ang detalye' },
  'Hide breakdown': { he: 'הסתר פירוט', ur: 'تفصیل چھپائیں', tl: 'Itago ang detalye' },
  'Base pay': { he: 'שכר בסיס', ur: 'بنیادی تنخواہ', tl: 'Batayang sahod' },
  Overtime: { he: 'שעות נוספות', ur: 'اوور ٹائم', tl: 'Overtime' },
  Holiday: { he: 'חג', ur: 'تعطیل', tl: 'Pista opisyal' },
  Bonus: { he: 'בונוס', ur: 'بونس', tl: 'Bonus' },
  Tip: { he: 'טיפ', ur: 'ٹپ', tl: 'Tip' },
  Gross: { he: 'ברוטו', ur: 'مجموعی', tl: 'Gross' },
  'Take-home': { he: 'נטו לתשלום', ur: 'خالص وصول', tl: 'Uuwiing halaga' },
  Deduction: { he: 'ניכוי', ur: 'کٹوتی', tl: 'Bawas' },
  Deductions: { he: 'ניכויים', ur: 'کٹوتیاں', tl: 'Mga bawas' },
  'Unpaid absence': {
    he: 'היעדרות ללא תשלום',
    ur: 'بلا معاوضہ غیر حاضری',
    tl: 'Absent na walang bayad',
  },
  'Advance already paid': {
    he: 'מקדמה ששולמה כבר',
    ur: 'پیشگی جو ادا ہو چکی',
    tl: 'Paunang naibigay na',
  },
  'Salary advance already given': {
    he: 'מקדמת משכורת שכבר ניתנה',
    ur: 'تنخواہ کی پیشگی جو دی جا چکی',
    tl: 'Paunang sahod na naibigay na',
  },
  'Loan repayment': { he: 'החזר הלוואה', ur: 'قرض کی واپسی', tl: 'Bayad sa utang' },
  Loan: { he: 'הלוואה', ur: 'قرض', tl: 'Utang' },
  left: { he: 'נותר', ur: 'باقی', tl: 'natitira' },
  mo: { he: 'חודש', ur: 'ماہ', tl: 'buwan' },
  'Set plan': { he: 'קבע תוכנית', ur: 'منصوبہ بنائیں', tl: 'Magtakda ng plano' },
  'Repayment plan': { he: 'תוכנית החזר', ur: 'واپسی کا منصوبہ', tl: 'Plano ng pagbabayad' },
  Outstanding: { he: 'יתרה', ur: 'باقی رقم', tl: 'Natitirang utang' },
  'Take off each payslip': {
    he: 'נכה מכל תלוש',
    ur: 'ہر تنخواہ سے کاٹیں',
    tl: 'Ibawas sa bawat payslip',
  },
  'Leave empty to settle by hand': {
    he: 'השאר ריק כדי לסגור ידנית',
    ur: 'ہاتھ سے طے کرنے کے لیے خالی چھوڑیں',
    tl: 'Iwanang blangko para manwal na ayusin',
  },
  'Paid off in about {{n}} months.': {
    he: 'ייסגר בערך תוך {{n}} חודשים.',
    ur: 'تقریباً {{n}} مہینوں میں ادا ہو جائے گا۔',
    tl: 'Mababayaran sa humigit-kumulang {{n}} buwan.',
  },
  'Deduct automatically every payroll': {
    he: 'נכה אוטומטית בכל תלוש',
    ur: 'ہر تنخواہ پر خود بخود کاٹیں',
    tl: 'Awtomatikong ibawas kada sahod',
  },
  'Log hours': { he: 'רשום שעות', ur: 'گھنٹے درج کریں', tl: 'Itala ang oras' },
  'Log my hours': { he: 'רשום את השעות שלי', ur: 'میرے گھنٹے درج کریں', tl: 'Itala ang oras ko' },
  'Hours logged': { he: 'שעות שנרשמו', ur: 'درج شدہ گھنٹے', tl: 'Mga naitalang oras' },
  'No hours logged this month.': {
    he: 'לא נרשמו שעות החודש.',
    ur: 'اس مہینے کوئی گھنٹے درج نہیں ہوئے۔',
    tl: 'Walang naitalang oras ngayong buwan.',
  },
  Hours: { he: 'שעות', ur: 'گھنٹے', tl: 'Mga oras' },
  'Regular hours': { he: 'שעות רגילות', ur: 'عام گھنٹے', tl: 'Karaniwang oras' },
  'Sick leave': { he: 'מחלה', ur: 'بیماری کی چھٹی', tl: 'Sick leave' },
  Vacation: { he: 'חופשה', ur: 'چھٹی', tl: 'Bakasyon' },
  'Bonus / deduction': { he: 'בונוס / ניכוי', ur: 'بونس / کٹوتی', tl: 'Bonus / bawas' },
  'Change pay': { he: 'שנה שכר', ur: 'تنخواہ بدلیں', tl: 'Baguhin ang sahod' },
  'Payroll history': { he: 'היסטוריית שכר', ur: 'تنخواہ کی تاریخ', tl: 'Kasaysayan ng sahod' },
  'Pay for {{name}}': { he: 'שכר ל־{{name}}', ur: '{{name}} کی تنخواہ', tl: 'Sahod ni {{name}}' },
  Monthly: { he: 'חודשי', ur: 'ماہانہ', tl: 'Buwanan' },
  Hourly: { he: 'שעתי', ur: 'فی گھنٹہ', tl: 'Oras-oras' },
  'Salary per month': { he: 'משכורת לחודש', ur: 'ماہانہ تنخواہ', tl: 'Sahod kada buwan' },
  'Rate per day': { he: 'תעריף ליום', ur: 'روزانہ ریٹ', tl: 'Bayad kada araw' },
  'Rate per hour': { he: 'תעריף לשעה', ur: 'فی گھنٹہ ریٹ', tl: 'Bayad kada oras' },
  'Overtime multiplier': {
    he: 'מכפיל שעות נוספות',
    ur: 'اوور ٹائم ضرب',
    tl: 'Multiplier ng overtime',
  },
  'Hours in a normal day': {
    he: 'שעות ביום רגיל',
    ur: 'عام دن میں گھنٹے',
    tl: 'Oras sa karaniwang araw',
  },
  'Days in a normal month': {
    he: 'ימים בחודש רגיל',
    ur: 'عام مہینے میں دن',
    tl: 'Araw sa karaniwang buwan',
  },
  'In effect from': { he: 'בתוקף מ־', ur: 'سے نافذ', tl: 'Epektibo mula' },
  'Earlier payslips keep the terms that were in force when they were paid.': {
    he: 'תלושים קודמים שומרים על התנאים שהיו בתוקף בזמן התשלום.',
    ur: 'پرانی تنخواہیں انہی شرائط پر رہتی ہیں جو ادائیگی کے وقت نافذ تھیں۔',
    tl: 'Ang mga dating payslip ay nananatili sa kasunduang umiral noong binayaran.',
  },
  'Adjust {{name}}’s pay': {
    he: 'עדכן את השכר של {{name}}',
    ur: '{{name}} کی تنخواہ ایڈجسٹ کریں',
    tl: 'Ayusin ang sahod ni {{name}}',
  },
  Amount: { he: 'סכום', ur: 'رقم', tl: 'Halaga' },
  'What is this for?': { he: 'בשביל מה זה?', ur: 'یہ کس لیے ہے؟', tl: 'Para saan ito?' },
  'Note (optional)': { he: 'הערה (לא חובה)', ur: 'نوٹ (اختیاری)', tl: 'Tala (opsyonal)' },
  'Never take more than this share of a payslip for loans (%)': {
    he: 'לעולם אל תיקח יותר מהחלק הזה מהתלוש עבור הלוואות (%)',
    ur: 'قرض کے لیے تنخواہ سے اس حصے سے زیادہ کبھی نہ کاٹیں (%)',
    tl: 'Huwag kailanman kumuha ng higit dito sa payslip para sa utang (%)',
  },
  'Pay approved reimbursements with the salary': {
    he: 'שלם החזרי הוצאות מאושרים יחד עם המשכורת',
    ur: 'منظور شدہ اخراجات کی واپسی تنخواہ کے ساتھ ادا کریں',
    tl: 'Bayaran ang aprubadong reimbursement kasama ng sahod',
  },
  'Finalize this payroll? Hours for the period will be locked and loan repayments will be recorded.':
    {
      he: 'לסגור את השכר סופית? השעות לתקופה יינעלו והחזרי ההלוואות יירשמו.',
      ur: 'اس تنخواہ کو حتمی کریں؟ اس مدت کے گھنٹے مقفل ہو جائیں گے اور قرض کی واپسی درج ہو گی۔',
      tl: 'Tapusin ang sahod na ito? Mala-lock ang mga oras at maitatala ang bayad-utang.',
    },
  'How was it paid?': { he: 'איך שולם?', ur: 'کیسے ادا کیا گیا؟', tl: 'Paano binayaran?' },
  'bank transfer': { he: 'העברה בנקאית', ur: 'بینک ٹرانسفر', tl: 'bank transfer' },
  'Discard this draft payroll?': {
    he: 'למחוק את טיוטת השכר?',
    ur: 'اس مسودہ تنخواہ کو ضائع کریں؟',
    tl: 'Itapon ang burador ng sahod?',
  },

  // === Staff: my pay ===
  'My pay': { he: 'השכר שלי', ur: 'میری تنخواہ', tl: 'Sahod ko' },
  'My rate': { he: 'התעריף שלי', ur: 'میرا ریٹ', tl: 'Rate ko' },
  'Overtime is paid at ×{{x}}': {
    he: 'שעות נוספות משולמות ב־×{{x}}',
    ur: 'اوور ٹائم ×{{x}} پر ادا ہوتا ہے',
    tl: 'Ang overtime ay binabayaran ng ×{{x}}',
  },
  'My loans': { he: 'ההלוואות שלי', ur: 'میرے قرضے', tl: 'Mga utang ko' },
  '{{amount}} comes off each salary automatically.': {
    he: '{{amount}} יורד מכל משכורת אוטומטית.',
    ur: 'ہر تنخواہ سے {{amount}} خود بخود کٹتا ہے۔',
    tl: 'Awtomatikong nababawas ang {{amount}} sa bawat sahod.',
  },
  'No automatic deduction.': {
    he: 'אין ניכוי אוטומטי.',
    ur: 'کوئی خودکار کٹوتی نہیں۔',
    tl: 'Walang awtomatikong bawas.',
  },
  'My payslips': { he: 'התלושים שלי', ur: 'میری تنخواہ کی پرچیاں', tl: 'Mga payslip ko' },
  'No payslips yet.': {
    he: 'אין עדיין תלושים.',
    ur: 'ابھی کوئی پرچی نہیں۔',
    tl: 'Wala pang payslip.',
  },
  'Money you spent, returned': {
    he: 'כסף שהוצאת, מוחזר',
    ur: 'آپ کے خرچ کیے پیسے، واپس',
    tl: 'Pera mong ginastos, ibinalik',
  },
  'I received this': { he: 'קיבלתי את זה', ur: 'مجھے یہ مل گیا', tl: 'Natanggap ko ito' },
  'Receipt confirmed': {
    he: 'הקבלה אושרה',
    ur: 'وصولی کی تصدیق ہوئی',
    tl: 'Nakumpirma ang pagtanggap',
  },
  'This month is still being prepared.': {
    he: 'החודש הזה עדיין בהכנה.',
    ur: 'یہ مہینہ ابھی تیار ہو رہا ہے۔',
    tl: 'Inihahanda pa ang buwang ito.',
  },
  'Your employer is notified straight away when you log overtime.': {
    he: 'המעסיק מקבל התראה מיד כשאתה רושם שעות נוספות.',
    ur: 'جب آپ اوور ٹائم درج کرتے ہیں تو آپ کے آجر کو فوراً اطلاع جاتی ہے۔',
    tl: 'Agad na naaabisuhan ang employer mo kapag nag-log ka ng overtime.',
  },

  // === Staff: history ===
  'My history': { he: 'ההיסטוריה שלי', ur: 'میری تاریخ', tl: 'Kasaysayan ko' },
  'Nothing finished yet. Your completed work shows here.': {
    he: 'עדיין לא הושלם כלום. העבודות שסיימת יופיעו כאן.',
    ur: 'ابھی کچھ مکمل نہیں ہوا۔ آپ کا مکمل کام یہاں آئے گا۔',
    tl: 'Wala pang natatapos. Dito lalabas ang tapos mong trabaho.',
  },
  Yesterday: { he: 'אתמול', ur: 'کل', tl: 'Kahapon' },

  // === What a staff member is allowed to do ===
  'What they can do': {
    he: 'מה מותר להם',
    ur: 'وہ کیا کر سکتے ہیں',
    tl: 'Ano ang kaya nilang gawin',
  },
  'Hand out work': { he: 'לחלק עבודה', ur: 'کام تفویض کرنا', tl: 'Mamahagi ng trabaho' },
  'Create tasks and routines, assign them to the other staff, and keep the house rules.': {
    he: 'ליצור משימות ושגרות, להקצות אותן לשאר העובדים, ולתחזק את כללי הבית.',
    ur: 'کام اور معمولات بنانا، دوسرے عملے کو تفویض کرنا، اور گھر کے اصول سنبھالنا۔',
    tl: 'Gumawa ng gawain at routine, ibigay sa ibang tauhan, at panatilihin ang mga tuntunin ng bahay.',
  },
  'Manage the staff': {
    he: 'לנהל את העובדים',
    ur: 'عملہ سنبھالنا',
    tl: 'Pamahalaan ang mga tauhan',
  },
  'Add people, reset their passwords, and disable someone who has left.': {
    he: 'להוסיף אנשים, לאפס להם סיסמאות, ולהשבית מי שעזב.',
    ur: 'لوگ شامل کرنا، ان کے پاس ورڈ ری سیٹ کرنا، اور جانے والے کو بند کرنا۔',
    tl: 'Magdagdag ng tao, i-reset ang password nila, at i-disable ang umalis.',
  },
  'See the spending': { he: 'לראות את ההוצאות', ur: 'اخراجات دیکھنا', tl: 'Makita ang gastos' },
  'Household expenses and the receipts waiting to be reviewed. Not salaries.': {
    he: 'הוצאות הבית והקבלות שממתינות לבדיקה. לא משכורות.',
    ur: 'گھر کے اخراجات اور جائزے کی منتظر رسیدیں۔ تنخواہیں نہیں۔',
    tl: 'Gastos ng bahay at mga resibong hinihintay suriin. Hindi ang sahod.',
  },
  'Salaries and household settings stay with you.': {
    he: 'משכורות והגדרות הבית נשארות אצלך.',
    ur: 'تنخواہیں اور گھر کی ترتیبات آپ کے پاس رہتی ہیں۔',
    tl: 'Ang sahod at mga setting ng bahay ay nananatili sa iyo.',
  },

  // === Turning on push ===
  'Turn on notifications': { he: 'הפעל התראות', ur: 'اطلاعات آن کریں', tl: 'I-on ang mga abiso' },
  'Otherwise you only find out about new work when you open the app.': {
    he: 'אחרת תגלה על עבודה חדשה רק כשתפתח את האפליקציה.',
    ur: 'ورنہ آپ کو نئے کام کا پتہ صرف ایپ کھولنے پر چلے گا۔',
    tl: 'Kung hindi, malalaman mo lang ang bagong trabaho kapag binuksan mo ang app.',
  },
  'Turn on': { he: 'הפעל', ur: 'آن کریں', tl: 'I-on' },
  'Not now': { he: 'לא עכשיו', ur: 'ابھی نہیں', tl: 'Hindi ngayon' },

  // === Receipts a staff member sent ===
  'Receipts I sent': {
    he: 'קבלות ששלחתי',
    ur: 'میں نے بھیجی رسیدیں',
    tl: 'Mga resibong ipinadala ko',
  },
  'Reading it…': { he: 'קורא אותה…', ur: 'پڑھ رہے ہیں…', tl: 'Binabasa…' },
  'Read — confirm it': { he: 'נקראה — אשר', ur: 'پڑھ لی — تصدیق کریں', tl: 'Nabasa — kumpirmahin' },
  'Needs your answer': {
    he: 'ממתין לתשובה שלך',
    ur: 'آپ کے جواب کا منتظر',
    tl: 'Kailangan ng sagot mo',
  },
  'Waiting for the boss': { he: 'ממתין לבוס', ur: 'مالک کے انتظار میں', tl: 'Hinihintay ang amo' },
  'Could not read — send it again': {
    he: 'לא הצלחנו לקרוא — שלח שוב',
    ur: 'پڑھ نہیں سکے — دوبارہ بھیجیں',
    tl: 'Hindi nabasa — ipadala ulit',
  },

  // === Notifications inbox ===
  'Mark all read': {
    he: 'סמן הכל כנקרא',
    ur: 'سب پڑھا ہوا نشان زد کریں',
    tl: 'Markahang lahat ng nabasa',
  },
  'Nothing new.': { he: 'אין חדש.', ur: 'کچھ نیا نہیں۔', tl: 'Walang bago.' },

  // === Pulse: the week ahead ===
  'What’s coming': { he: 'מה מגיע', ur: 'کیا آنے والا ہے', tl: 'Ano ang paparating' },
  'Next {{n}} days': { he: '{{n}} הימים הבאים', ur: 'اگلے {{n}} دن', tl: 'Susunod na {{n}} araw' },
  'Nothing scheduled in this window.': {
    he: 'אין שום דבר מתוכנן בטווח הזה.',
    ur: 'اس مدت میں کچھ طے شدہ نہیں۔',
    tl: 'Walang nakatakda sa panahong ito.',
  },
  'Nothing left today': {
    he: 'לא נשאר כלום היום',
    ur: 'آج کچھ باقی نہیں',
    tl: 'Wala nang natitira ngayon',
  },
  '{{n}} today': { he: '{{n}} היום', ur: 'آج {{n}}', tl: '{{n}} ngayong araw' },
  'Tasks, routines and events': {
    he: 'משימות, שגרות ואירועים',
    ur: 'کام، معمولات اور تقریبات',
    tl: 'Mga gawain, routine at kaganapan',
  },
  'Add event': { he: 'הוסף אירוע', ur: 'تقریب شامل کریں', tl: 'Magdagdag ng kaganapan' },
  'Remove this event?': {
    he: 'להסיר את האירוע?',
    ur: 'یہ تقریب ہٹا دیں؟',
    tl: 'Alisin ang kaganapang ito?',
  },
  Event: { he: 'אירוע', ur: 'تقریب', tl: 'Kaganapan' },
  Appointment: { he: 'פגישה', ur: 'ملاقات', tl: 'Appointment' },
  Delivery: { he: 'משלוח', ur: 'ترسیل', tl: 'Delivery' },
  Maintenance: { he: 'תחזוקה', ur: 'دیکھ بھال', tl: 'Pagpapanatili' },
  Errand: { he: 'סידור', ur: 'کام کاج', tl: 'Lakad' },
  'Where?': { he: 'איפה?', ur: 'کہاں؟', tl: 'Saan?' },
  Details: { he: 'פרטים', ur: 'تفصیلات', tl: 'Mga detalye' },

  // === A co-owner joining a house that already exists ===
  'Join {{name}}': {
    he: 'הצטרפות ל{{name}}',
    ur: '{{name}} میں شامل ہوں',
    tl: 'Sumali sa {{name}}',
  },
  'The house is already here. Three questions and you are in.': {
    he: 'הבית כבר קיים. שלוש שאלות ואתה בפנים.',
    ur: 'گھر پہلے سے موجود ہے۔ تین سوال اور آپ اندر ہیں۔',
    tl: 'Nandiyan na ang bahay. Tatlong tanong at pasok ka na.',
  },

  // === Contacts: a number you can tap, and who is allowed to see it ===
  'Call {{number}}': { he: 'חייג {{number}}', ur: 'کال {{number}}', tl: 'Tawagan {{number}}' },
  'Phone (optional) — makes this tap-to-call': {
    he: 'טלפון (לא חובה) — הופך את זה ללחיצה אחת לחיוג',
    ur: 'فون (اختیاری) — ایک ٹیپ سے کال',
    tl: 'Telepono (opsyonal) — para matawagan sa isang tap',
  },
  'Who sees this?': {
    he: 'מי רואה את זה?',
    ur: 'یہ کون دیکھے گا؟',
    tl: 'Sino ang makakakita nito?',
  },
  'Everyone in the house': {
    he: 'כל מי שבבית',
    ur: 'گھر میں سب',
    tl: 'Lahat sa bahay',
  },
  'Only {{n}} people see this': {
    he: 'רק {{n}} אנשים רואים את זה',
    ur: 'صرف {{n}} افراد یہ دیکھتے ہیں',
    tl: '{{n}} tao lang ang nakakakita nito',
  },
  'You always see everything you wrote.': {
    he: 'אתה תמיד רואה את כל מה שכתבת.',
    ur: 'آپ نے جو لکھا وہ آپ ہمیشہ دیکھیں گے۔',
    tl: 'Lagi mong nakikita ang lahat ng isinulat mo.',
  },

  // === Late work ===
  // Set by the server, not guessed by the screen. These two states existed in
  // the database from the first migration and nothing could ever reach them.
  Late: { he: 'באיחור', ur: 'تاخیر سے', tl: 'Huli na' },
  'Still not done': { he: 'עדיין לא בוצע', ur: 'ابھی تک نہیں ہوا', tl: 'Hindi pa rin tapos' },
  'This is late': { he: 'זה באיחור', ur: 'یہ تاخیر سے ہے', tl: 'Huli na ito' },

  // === Leave balance ===
  // Days off are a statutory entitlement in the UAE, not a favour, so the
  // person who earned them reads the number on their own screen.
  'Leave left': { he: 'ימי חופשה שנותרו', ur: 'باقی چھٹیاں', tl: 'Natitirang leave' },
  '{{left}} of {{total}} days': {
    he: '{{left}} מתוך {{total}} ימים',
    ur: '{{total}} میں سے {{left}} دن',
    tl: '{{left}} sa {{total}} araw',
  },
  '{{taken}} taken of {{total}} days, since {{start}}': {
    he: 'נוצלו {{taken}} מתוך {{total}} ימים, מאז {{start}}',
    ur: '{{start}} سے اب تک {{total}} میں سے {{taken}} دن لیے گئے',
    tl: '{{taken}} sa {{total}} araw ang nagamit, mula {{start}}',
  },
  'Sick days do not come out of this: {{n}} this year': {
    he: 'ימי מחלה לא יורדים מכאן: {{n}} השנה',
    ur: 'بیماری کی چھٹیاں اس میں شامل نہیں: اس سال {{n}}',
    tl: 'Hindi kasama rito ang sick leave: {{n}} ngayong taon',
  },
  'Leave — {{name}}': { he: 'חופשה — {{name}}', ur: 'چھٹیاں — {{name}}', tl: 'Leave — {{name}}' },
  'Taken this year': { he: 'נוצלו השנה', ur: 'اس سال لیے گئے', tl: 'Nagamit ngayong taon' },
  Left: { he: 'נותרו', ur: 'باقی', tl: 'Natitira' },
  'Sick days (separate)': {
    he: 'ימי מחלה (נפרד)',
    ur: 'بیماری کی چھٹیاں (الگ)',
    tl: 'Sick days (hiwalay)',
  },
  'Counted from {{start}}': {
    he: 'נספר מאז {{start}}',
    ur: '{{start}} سے شمار',
    tl: 'Binibilang mula {{start}}',
  },
  'Paid days off per year': {
    he: 'ימי חופשה בתשלום בשנה',
    ur: 'سالانہ تنخواہ کے ساتھ چھٹیاں',
    tl: 'Bayad na araw ng leave bawat taon',
  },
  'Started on': { he: 'תאריך תחילת עבודה', ur: 'کام شروع کرنے کی تاریخ', tl: 'Nagsimula noong' },
  'The leave year runs from this date, not from January.': {
    he: 'שנת החופשה נספרת מהתאריך הזה, לא מינואר.',
    ur: 'چھٹیوں کا سال اسی تاریخ سے شروع ہوتا ہے، جنوری سے نہیں۔',
    tl: 'Ang taon ng leave ay mula sa petsang ito, hindi mula Enero.',
  },

  // === One event and the tasks hanging off it ===
  // A dinner for eight is a shop, a table, a car and a guest room. Both roles
  // read this screen: the housekeeper with one job for Thursday sees what
  // Thursday is.
  'Add task': { he: 'הוסף משימה', ur: 'کام شامل کریں', tl: 'Magdagdag ng gawain' },
  'Part of': { he: 'שייך ל־', ur: 'کا حصہ', tl: 'Bahagi ng' },
  'Event not found': {
    he: 'האירוע לא נמצא',
    ur: 'تقریب نہیں ملی',
    tl: 'Hindi nakita ang kaganapan',
  },
  'No tasks yet': { he: 'עוד אין משימות', ur: 'ابھی کوئی کام نہیں', tl: 'Wala pang gawain' },
  '{{done}} of {{total}} done': {
    he: '{{done}} מתוך {{total}} הושלמו',
    ur: '{{total}} میں سے {{done}} مکمل',
    tl: '{{done}} sa {{total}} tapos na',
  },
  'Add the jobs this event needs — the shop, the table, the car.': {
    he: 'הוסף את המשימות שהאירוע דורש — הקניות, השולחן, הרכב.',
    ur: 'اس تقریب کے لیے درکار کام شامل کریں — خریداری، میز، گاڑی۔',
    tl: 'Idagdag ang mga gawaing kailangan nito — ang pamimili, ang mesa, ang sasakyan.',
  },
  'Nothing assigned for this event yet.': {
    he: 'עוד לא הוקצתה משימה לאירוע הזה.',
    ur: 'اس تقریب کے لیے ابھی کچھ تفویض نہیں ہوا۔',
    tl: 'Wala pang naitalagang gawain para dito.',
  },
  'Remove this event': { he: 'הסר את האירוע', ur: 'تقریب ہٹا دیں', tl: 'Alisin ang kaganapan' },
  'The tasks stay — only the event they were grouped under goes away.': {
    he: 'המשימות נשארות — רק האירוע שקיבץ אותן נמחק.',
    ur: 'کام باقی رہیں گے — صرف وہ تقریب ہٹے گی جس کے تحت وہ جمع تھے۔',
    tl: 'Mananatili ang mga gawain — ang kaganapan lang ang aalisin.',
  },

  // === The staff screen ===
  // Open to a staff member with `manage_staff` now, not only the owner, so
  // every string here has to exist in their language. It was the last screen
  // written straight in Hebrew.
  'The household team': { he: 'צוות הבית', ur: 'گھر کی ٹیم', tl: 'Ang koponan ng bahay' },
  'Add a person': { he: 'הוסף עובד', ur: 'فرد شامل کریں', tl: 'Magdagdag ng tao' },
  'New staff member': { he: 'עובד חדש', ur: 'نیا ملازم', tl: 'Bagong tauhan' },
  'No people yet.': { he: 'אין עדיין עובדים.', ur: 'ابھی کوئی نہیں۔', tl: 'Wala pang tao.' },
  'Add the first person so you can start handing out work.': {
    he: 'הוסף את העובד הראשון כדי להתחיל לחלק עבודה.',
    ur: 'کام تفویض کرنا شروع کرنے کے لیے پہلا فرد شامل کریں۔',
    tl: 'Magdagdag ng unang tao para makapagbigay ka na ng trabaho.',
  },
  Disabled: { he: 'מושבת', ur: 'غیر فعال', tl: 'Naka-disable' },
  Enable: { he: 'הפעל', ur: 'فعال کریں', tl: 'Paganahin' },
  'Reset password': { he: 'איפוס סיסמה', ur: 'پاس ورڈ ری سیٹ', tl: 'I-reset ang password' },
  'Generate a new password?': {
    he: 'להפיק סיסמה חדשה?',
    ur: 'نیا پاس ورڈ بنائیں؟',
    tl: 'Gumawa ng bagong password?',
  },
  'New password': { he: 'סיסמה חדשה', ur: 'نیا پاس ورڈ', tl: 'Bagong password' },
  'Hand it over in person.': {
    he: 'מסור באופן אישי.',
    ur: 'ذاتی طور پر دیں۔',
    tl: 'Ibigay nang personal.',
  },
  'Sign-in details': { he: 'פרטי כניסה', ur: 'سائن اِن تفصیلات', tl: 'Mga detalye ng pag-sign in' },
  'Hand these over in person. The password is not shown again.': {
    he: 'מסור באופן אישי. הסיסמה לא תוצג שוב.',
    ur: 'یہ ذاتی طور پر دیں۔ پاس ورڈ دوبارہ نہیں دکھایا جائے گا۔',
    tl: 'Ibigay ito nang personal. Hindi na ipapakita muli ang password.',
  },
  Copy: { he: 'העתק', ur: 'کاپی', tl: 'Kopyahin' },
  Copied: { he: 'הועתק', ur: 'کاپی ہو گیا', tl: 'Nakopya' },
  'Send on WhatsApp': { he: 'שלח בוואטסאפ', ur: 'واٹس ایپ پر بھیجیں', tl: 'Ipadala sa WhatsApp' },
  Username: { he: 'שם משתמש', ur: 'صارف نام', tl: 'Username' },
  'First password': { he: 'סיסמה ראשונה', ur: 'پہلا پاس ورڈ', tl: 'Unang password' },
  'Open the app and sign in. You will be asked to choose your own password.': {
    he: 'פתח את האפליקציה והיכנס. תתבקש לבחור סיסמה משלך.',
    ur: 'ایپ کھولیں اور سائن اِن کریں۔ آپ سے اپنا پاس ورڈ منتخب کرنے کو کہا جائے گا۔',
    tl: 'Buksan ang app at mag-sign in. Hihilingin sa iyong pumili ng sarili mong password.',
  },
  // Job positions
  Driver: { he: 'נהג', ur: 'ڈرائیور', tl: 'Drayber' },
  'Personal assistant': { he: 'עוזר אישי', ur: 'ذاتی معاون', tl: 'Personal assistant' },
  Housekeeper: { he: 'עוזרת בית', ur: 'گھریلو ملازمہ', tl: 'Katulong sa bahay' },
  Gardener: { he: 'גנן', ur: 'مالی', tl: 'Hardinero' },
  Nanny: { he: 'מטפלת', ur: 'آیا', tl: 'Yaya' },
  Cook: { he: 'טבח', ur: 'باورچی', tl: 'Kusinero' },

  // === House rules (memory) ===
  // Read by everyone in the household, written by the owner and by any staff
  // member with `assign_work`. The reader is usually the person whose first
  // language is not the owner's, so every string here matters.
  Rules: { he: 'חוקים', ur: 'اصول', tl: 'Mga tuntunin' },
  'House rules': { he: 'חוקי הבית', ur: 'گھر کے اصول', tl: 'Mga tuntunin ng bahay' },
  'How this house likes things done.': {
    he: 'איך אוהבים לעשות דברים בבית הזה.',
    ur: 'اس گھر میں کام کیسے کیے جاتے ہیں۔',
    tl: 'Kung paano ginagawa ang mga bagay sa bahay na ito.',
  },
  'Always remember': { he: 'תמיד לזכור', ur: 'ہمیشہ یاد رکھیں', tl: 'Laging tandaan' },
  'The rest': { he: 'השאר', ur: 'باقی', tl: 'Ang iba pa' },
  'No house rules yet.': {
    he: 'עדיין אין חוקי בית.',
    ur: 'ابھی تک گھر کے کوئی اصول نہیں۔',
    tl: 'Wala pang mga tuntunin ng bahay.',
  },
  'Add a rule': { he: 'הוסף חוק', ur: 'اصول شامل کریں', tl: 'Magdagdag ng tuntunin' },
  'New house rule': { he: 'חוק בית חדש', ur: 'گھر کا نیا اصول', tl: 'Bagong tuntunin ng bahay' },
  'Edit rule': { he: 'עריכת חוק', ur: 'اصول میں ترمیم', tl: 'I-edit ang tuntunin' },
  'Delete this rule?': {
    he: 'למחוק את החוק?',
    ur: 'یہ اصول حذف کریں؟',
    tl: 'Burahin ang tuntuning ito?',
  },
  'Short title': { he: 'כותרת קצרה', ur: 'مختصر عنوان', tl: 'Maikling pamagat' },
  'What the house needs to remember': {
    he: 'מה שהבית צריך לזכור',
    ur: 'جو گھر کو یاد رکھنا ہے',
    tl: 'Ang dapat tandaan ng bahay',
  },
  'Pin to the top — everyone sees this first': {
    he: 'הצמד למעלה — כולם רואים את זה ראשון',
    ur: 'اوپر لگائیں — سب سے پہلے یہی نظر آئے گا',
    tl: 'I-pin sa itaas — ito ang unang makikita ng lahat',
  },
  'Only 8 rules can be pinned. Unpin one first.': {
    he: 'אפשר להצמיד עד 8 חוקים. שחרר אחד קודם.',
    ur: 'صرف 8 اصول اوپر لگ سکتے ہیں۔ پہلے ایک ہٹائیں۔',
    tl: 'Walong tuntunin lang ang maaaring i-pin. Mag-unpin muna ng isa.',
  },
  Pin: { he: 'הצמד', ur: 'اوپر لگائیں', tl: 'I-pin' },
  Unpin: { he: 'שחרר', ur: 'ہٹائیں', tl: 'I-unpin' },
  Pinned: { he: 'מוצמד', ur: 'اوپر لگا ہوا', tl: 'Naka-pin' },
  'Saving failed': { he: 'השמירה נכשלה', ur: 'محفوظ کرنا ناکام', tl: 'Nabigo ang pag-save' },
  // Memory categories
  Shopping: { he: 'קניות', ur: 'خریداری', tl: 'Pamimili' },
  Car: { he: 'רכב', ur: 'گاڑی', tl: 'Sasakyan' },
  Guests: { he: 'אורחים', ur: 'مہمان', tl: 'Mga bisita' },
  Deliveries: { he: 'משלוחים', ur: 'ترسیلات', tl: 'Mga delivery' },
  Contacts: { he: 'אנשי קשר', ur: 'رابطے', tl: 'Mga contact' },

  // === Standing arrangements, loans, and how the salary is handed over ===
  // Read by the person being paid as much as by the one paying. Every line
  // here is about money leaving or reaching somebody's hand, so it says what
  // happens rather than naming a setting.
  'Monthly deduction': {
    he: 'ניכוי חודשי',
    ur: 'ماہانہ کٹوتی',
    tl: 'Buwanang kaltas',
  },
  'Every month from {{name}}': {
    he: 'כל חודש מ{{name}}',
    ur: '{{name}} سے ہر مہینے',
    tl: 'Bawat buwan mula kay {{name}}',
  },
  'Set once. It comes off every payslip until you stop it.': {
    he: 'מגדירים פעם אחת. יורד מכל תלוש עד שעוצרים.',
    ur: 'ایک بار مقرر کریں۔ روکنے تک ہر تنخواہ سے کٹتا رہے گا۔',
    tl: 'Isang beses lang itakda. Kakaltasin sa bawat sahod hanggang ihinto mo.',
  },
  'What is it for': { he: 'בשביל מה זה', ur: 'یہ کس لیے ہے', tl: 'Para saan ito' },
  Accommodation: { he: 'מגורים', ur: 'رہائش', tl: 'Tirahan' },
  'Amount each month': { he: 'סכום כל חודש', ur: 'ہر مہینے کی رقم', tl: 'Halaga bawat buwan' },
  From: { he: 'מתאריך', ur: 'اس تاریخ سے', tl: 'Mula' },
  'Until (optional)': { he: 'עד (לא חובה)', ur: 'تک (اختیاری)', tl: 'Hanggang (opsyonal)' },
  'every month': { he: 'כל חודש', ur: 'ہر مہینے', tl: 'bawat buwan' },
  'from {{date}}': { he: 'מ־{{date}}', ur: '{{date}} سے', tl: 'mula {{date}}' },
  'until {{date}}': { he: 'עד {{date}}', ur: '{{date}} تک', tl: 'hanggang {{date}}' },
  Stop: { he: 'עצור', ur: 'روکیں', tl: 'Ihinto' },
  'Stop taking {{label}} off every month?': {
    he: 'להפסיק לנכות {{label}} כל חודש?',
    ur: 'ہر مہینے {{label}} کاٹنا بند کریں؟',
    tl: 'Ihinto ang buwanang pagkaltas ng {{label}}?',
  },

  'Add loan': { he: 'הוסף הלוואה', ur: 'قرض شامل کریں', tl: 'Magdagdag ng utang' },
  'Money lent to {{name}}': {
    he: 'כסף שהולווה ל{{name}}',
    ur: '{{name}} کو دیا گیا قرض',
    tl: 'Perang ipinahiram kay {{name}}',
  },
  'How much was lent': {
    he: 'כמה הולווה',
    ur: 'کتنا قرض دیا گیا',
    tl: 'Magkano ang ipinahiram',
  },
  'Handed over on': { he: 'נמסר בתאריך', ur: 'کس دن دیا گیا', tl: 'Ibinigay noong' },
  'How it comes back': {
    he: 'איך זה חוזר',
    ur: 'یہ کیسے واپس آئے گا',
    tl: 'Paano ito babalik',
  },
  'Start taking it from': {
    he: 'להתחיל לנכות מ־',
    ur: 'کٹوتی اس تاریخ سے شروع کریں',
    tl: 'Simulan ang pagkaltas mula',
  },
  'Leave empty to start with the next payroll.': {
    he: 'השאר ריק כדי להתחיל כבר בשכר הבא.',
    ur: 'اگلی تنخواہ سے شروع کرنے کے لیے خالی چھوڑ دیں۔',
    tl: 'Iwanang blangko para magsimula sa susunod na sahod.',
  },
  '{{amount}}/mo from {{date}}': {
    he: '{{amount}} לחודש מ־{{date}}',
    ur: '{{date}} سے {{amount}} ماہانہ',
    tl: '{{amount}}/buwan mula {{date}}',
  },
  '{{amount}} starts coming off your salary on {{date}}.': {
    he: '{{amount}} יתחיל לרדת מהמשכורת שלך ב־{{date}}.',
    ur: '{{date}} سے آپ کی تنخواہ سے {{amount}} کٹنا شروع ہوگا۔',
    tl: 'Magsisimulang kaltasin ang {{amount}} sa sahod mo sa {{date}}.',
  },

  'How the salary is handed over': {
    he: 'איך המשכורת נמסרת',
    ur: 'تنخواہ کیسے دی جاتی ہے',
    tl: 'Paano ibinibigay ang sahod',
  },
  'How this is handed over': {
    he: 'איך זה נמסר',
    ur: 'یہ کیسے دیا جاتا ہے',
    tl: 'Paano ito ibinibigay',
  },
  'How you are paid this': {
    he: 'איך משלמים לך את זה',
    ur: 'یہ آپ کو کیسے ادا کیا جاتا ہے',
    tl: 'Paano ito ibinabayad sa iyo',
  },
  'Leave the amount empty on one line and it takes whatever is left.': {
    he: 'השאר שורה אחת בלי סכום והיא תיקח את מה שנשאר.',
    ur: 'ایک لائن میں رقم خالی چھوڑ دیں، وہ باقی سب لے لے گی۔',
    tl: 'Iwanang blangko ang halaga sa isang linya at kukunin nito ang natitira.',
  },
  'What this part is': {
    he: 'מה החלק הזה',
    ur: 'یہ حصہ کیا ہے',
    tl: 'Ano ang bahaging ito',
  },
  'the rest': { he: 'השאר', ur: 'باقی', tl: 'ang natitira' },
  'Add a part': { he: 'הוסף חלק', ur: 'حصہ شامل کریں', tl: 'Magdagdag ng bahagi' },
  'Through the agency': {
    he: 'דרך הסוכנות',
    ur: 'ایجنسی کے ذریعے',
    tl: 'Sa pamamagitan ng ahensya',
  },
  'Bank transfer': { he: 'העברה בנקאית', ur: 'بینک ٹرانسفر', tl: 'Bank transfer' },
  'Cash in hand': { he: 'מזומן ליד', ur: 'نقد ہاتھ میں', tl: 'Cash sa kamay' },

  'What you pay on top': {
    he: 'מה שאתה משלם מעבר',
    ur: 'جو آپ اوپر سے ادا کرتے ہیں',
    tl: 'Ang binabayaran mo bukod pa',
  },
  'An agency fee or visa cost you carry. It is never taken from their salary.': {
    he: 'עמלת סוכנות או עלות ויזה שאתה נושא. זה אף פעם לא יורד מהמשכורת שלהם.',
    ur: 'ایجنسی فیس یا ویزا خرچ جو آپ اٹھاتے ہیں۔ یہ کبھی اُن کی تنخواہ سے نہیں کٹتا۔',
    tl: 'Bayad sa ahensya o gastos sa visa na ikaw ang sumasagot. Hindi ito kailanman kinakaltas sa sahod nila.',
  },
  'Agency fee': { he: 'עמלת סוכנות', ur: 'ایجنسی فیس', tl: 'Bayad sa ahensya' },
  'you pay this on top': {
    he: 'אתה משלם את זה מעבר',
    ur: 'یہ آپ اوپر سے ادا کرتے ہیں',
    tl: 'ikaw ang nagbabayad nito bukod pa',
  },
  '{{amount}} more you pay to agencies — not taken from anyone’s salary': {
    he: 'עוד {{amount}} שאתה משלם לסוכנויות — לא יורד מהמשכורת של אף אחד',
    ur: 'مزید {{amount}} جو آپ ایجنسیوں کو دیتے ہیں — کسی کی تنخواہ سے نہیں کٹتا',
    tl: 'Dagdag na {{amount}} na binabayaran mo sa mga ahensya — hindi kinakaltas sa sahod ninuman',
  },
  'The month costs the household {{amount}} in all': {
    he: 'החודש עולה לבית {{amount}} בסך הכול',
    ur: 'اس مہینے گھر پر کل {{amount}} خرچ آتا ہے',
    tl: 'Umaabot sa {{amount}} ang kabuuang gastos ng bahay ngayong buwan',
  },

  'Set pay': { he: 'קבע שכר', ur: 'تنخواہ مقرر کریں', tl: 'Itakda ang sahod' },
  'On trial for now': {
    he: 'בתקופת ניסיון בינתיים',
    ur: 'فی الحال آزمائشی مدت پر',
    tl: 'Nasa subok muna',
  },
  'On trial means no amount has been agreed yet. We stop asking until it is.': {
    he: 'תקופת ניסיון פירושה שעוד לא סוכם סכום. נפסיק לשאול עד שיסוכם.',
    ur: 'آزمائشی مدت کا مطلب ہے ابھی کوئی رقم طے نہیں ہوئی۔ طے ہونے تک ہم پوچھنا بند کر دیں گے۔',
    tl: 'Ibig sabihin ng nasa subok, wala pang napagkasunduang halaga. Hindi na kami magtatanong hangga’t wala pa.',
  },
  'On trial': { he: 'בתקופת ניסיון', ur: 'آزمائشی مدت پر', tl: 'Nasa subok' },
  'No pay agreed yet, so they are left out of payroll.': {
    he: 'עוד לא סוכם שכר, ולכן הם לא נכללים בשכר החודשי.',
    ur: 'ابھی تنخواہ طے نہیں ہوئی، اس لیے وہ تنخواہ کی فہرست میں شامل نہیں۔',
    tl: 'Wala pang napagkasunduang sahod, kaya hindi sila kasama sa payroll.',
  },
  'Agree on pay': { he: 'סכם שכר', ur: 'تنخواہ طے کریں', tl: 'Pagkasunduan ang sahod' },
  'End trial': { he: 'סיים ניסיון', ur: 'آزمائش ختم کریں', tl: 'Tapusin ang subok' },

  // === Owner Home — the queue ===
  'Requires decision': {
    he: 'דורש החלטה',
    ur: 'فیصلہ درکار',
    tl: 'Kailangan ng desisyon',
  },
  'Needs attention': { he: 'דורש תשומת לב', ur: 'توجہ درکار', tl: 'Kailangan ng atensyon' },
  'Worth a look': { he: 'שווה מבט', ur: 'ایک نظر ڈالیں', tl: 'Sulit tingnan' },
  'Running now': { he: 'קורה עכשיו', ur: 'ابھی جاری', tl: 'Ginagawa ngayon' },
  "Today's timeline": { he: 'ציר היום', ur: 'آج کی ترتیب', tl: 'Takbo ng araw' },
  'One thing needs you.': {
    he: 'דבר אחד מחכה לך.',
    ur: 'ایک چیز آپ کی منتظر ہے۔',
    tl: 'May isang bagay na naghihintay sa iyo.',
  },
  '{{n}} things need you.': {
    he: '{{n}} דברים מחכים לך.',
    ur: '{{n}} چیزیں آپ کی منتظر ہیں۔',
    tl: '{{n}} bagay ang naghihintay sa iyo.',
  },
  'All under control.': { he: 'הכול תחת שליטה.', ur: 'سب کچھ قابو میں ہے۔', tl: 'Ayos ang lahat.' },
  '{{n}} running now': {
    he: '{{n}} בעבודה עכשיו',
    ur: '{{n}} ابھی جاری',
    tl: '{{n}} ang ginagawa ngayon',
  },
  '{{n}} people': { he: '{{n}} אנשים', ur: '{{n}} افراد', tl: '{{n}} tao' },
  Someone: { he: 'מישהו', ur: 'کوئی', tl: 'May isang tao' },
  // Reads as "AED 150 for garden supplies" — a preposition, not a label.
  for: { he: 'עבור', ur: 'برائے', tl: 'para sa' },
  'Approve & dispense': {
    he: 'אשר ומסור',
    ur: 'منظور کریں اور دیں',
    tl: 'Aprubahan at ibigay',
  },
  'Mark as handed over': {
    he: 'סמן שנמסר',
    ur: 'نشان لگائیں کہ دے دیا',
    tl: 'Markahan bilang naibigay',
  },
  'Paid out of pocket': {
    he: 'שילם/ה מכיסו',
    ur: 'اپنی جیب سے ادا کیا',
    tl: 'Binayaran mula sa sariling bulsa',
  },
  'They have been reminded.': {
    he: 'נשלחה תזכורת.',
    ur: 'یاد دہانی بھیج دی گئی۔',
    tl: 'Naipaalala na sa kanila.',
  },
  '{{name}} is late — due {{when}}': {
    he: '{{name}} מאחר/ת — היה אמור {{when}}',
    ur: '{{name}} کو دیر ہو گئی — مقررہ وقت {{when}}',
    tl: 'Huli na si {{name}} — dapat {{when}}',
  },
  'Past its deadline': {
    he: 'עבר המועד',
    ur: 'مقررہ وقت گزر گیا',
    tl: 'Lampas na sa takdang oras',
  },
  'Suggested action': {
    he: 'מה כדאי לעשות',
    ur: 'تجویز کردہ اقدام',
    tl: 'Iminumungkahing hakbang',
  },
  'Nobody has picked this up. Ask for a status or hand it to someone else.': {
    he: 'אף אחד לא לקח את זה. אפשר לבקש עדכון או להעביר למישהו אחר.',
    ur: 'کسی نے یہ کام نہیں سنبھالا۔ صورتحال پوچھیں یا کسی اور کو دے دیں۔',
    tl: 'Walang kumuha nito. Humingi ng update o ibigay sa iba.',
  },
  Nudge: { he: 'תזכורת', ur: 'یاد دہانی', tl: 'Paalala' },
  'Nudge {{name}}': {
    he: 'שלח תזכורת ל{{name}}',
    ur: '{{name}} کو یاد دہانی بھیجیں',
    tl: 'Paalalahanan si {{name}}',
  },
  Open: { he: 'פתח', ur: 'کھولیں', tl: 'Buksan' },
  Unassigned: { he: 'לא שויך', ur: 'کسی کو نہیں دیا گیا', tl: 'Walang nakatalaga' },

  // === The composer ===
  'What would you like to happen?': {
    he: 'מה תרצה שיקרה?',
    ur: 'آپ کیا چاہتے ہیں کہ ہو؟',
    tl: 'Ano ang gusto mong mangyari?',
  },
  'Say it instead': { he: 'תגיד את זה', ur: 'بول کر بتائیں', tl: 'Sabihin na lang' },
  'Nobody yet': { he: 'עדיין אף אחד', ur: 'ابھی کوئی نہیں', tl: 'Wala pa' },
  When: { he: 'מתי', ur: 'کب', tl: 'Kailan' },
  'Pick a time': { he: 'בחר שעה', ur: 'وقت منتخب کریں', tl: 'Pumili ng oras' },
  'Pick a deadline': { he: 'בחר מועד אחרון', ur: 'آخری وقت منتخب کریں', tl: 'Pumili ng taning' },
  Checklist: { he: 'רשימה', ur: 'فہرست', tl: 'Listahan' },
  Clear: { he: 'נקה', ur: 'صاف کریں', tl: 'Burahin' },
  'Photo proof': { he: 'הוכחה בתמונה', ur: 'تصویری ثبوت', tl: 'Patunay na larawan' },
  Camera: { he: 'מצלמה', ur: 'کیمرہ', tl: 'Kamera' },
  'Schedule task': { he: 'תזמן משימה', ur: 'کام شیڈول کریں', tl: 'Iiskedyul ang gawain' },
  'Send task': { he: 'שלח משימה', ur: 'کام بھیجیں', tl: 'Ipadala ang gawain' },
  'Who is doing this?': { he: 'מי עושה את זה?', ur: 'یہ کون کرے گا؟', tl: 'Sino ang gagawa nito?' },
  'Schedule for': { he: 'תזמן ל', ur: 'کے لیے شیڈول', tl: 'Iiskedyul para sa' },
  'Extra detail': { he: 'פרטים נוספים', ur: 'اضافی تفصیل', tl: 'Karagdagang detalye' },

  // === Home digest ===
  Started: { he: 'התחיל/ה', ur: 'شروع کر دیا', tl: 'Sinimulan' },
  'Opened it': { he: 'פתח/ה', ur: 'کھول لیا', tl: 'Binuksan' },
  'Sent, not opened yet': {
    he: 'נשלח, עוד לא נפתח',
    ur: 'بھیج دیا، ابھی کھولا نہیں',
    tl: 'Naipadala, hindi pa binubuksan',
  },

  // === Money ===
  'Total house cost': {
    he: 'עלות הבית החודש',
    ur: 'گھر کی کل لاگت',
    tl: 'Kabuuang gastos ng bahay',
  },
  '{{n}}% vs last month': {
    he: '{{n}}% מול החודש שעבר',
    ur: 'پچھلے مہینے سے {{n}}%',
    tl: '{{n}}% kumpara noong nakaraang buwan',
  },
  '{{spend}} spent + {{pay}} in wages': {
    he: '{{spend}} הוצאות + {{pay}} שכר',
    ur: '{{spend}} اخراجات + {{pay}} تنخواہ',
    tl: '{{spend}} gastos + {{pay}} sahod',
  },
  'Nothing recorded this month yet.': {
    he: 'עוד לא נרשם כלום החודש.',
    ur: 'اس مہینے ابھی کچھ درج نہیں ہوا۔',
    tl: 'Wala pang naitala ngayong buwan.',
  },
  'Last month came to {{total}}. Tap to see it.': {
    he: 'החודש שעבר הסתכם ב־{{total}}. הקש כדי לראות.',
    ur: 'پچھلا مہینہ {{total}} رہا۔ دیکھنے کے لیے دبائیں۔',
    tl: 'Umabot sa {{total}} noong nakaraang buwan. Pindutin para makita.',
  },
  'Receipts and wages will show up here as they happen.': {
    he: 'קבלות ומשכורות יופיעו כאן כשהן קורות.',
    ur: 'رسیدیں اور تنخواہیں یہاں آتی جائیں گی۔',
    tl: 'Lalabas dito ang mga resibo at sahod habang nangyayari.',
  },
  'House expenses': { he: 'הוצאות הבית', ur: 'گھریلو اخراجات', tl: 'Gastos sa bahay' },
  Wages: { he: 'שכר', ur: 'تنخواہ', tl: 'Sahod' },
  'Still to pay': { he: 'עוד לתשלום', ur: 'ابھی ادا کرنا ہے', tl: 'Babayaran pa' },
  'Receipts to review': {
    he: 'קבלות לאישור',
    ur: 'جائزے کے لیے رسیدیں',
    tl: 'Mga resibong susuriin',
  },
  '{{n}} pending': { he: '{{n}} ממתינות', ur: '{{n}} زیر التوا', tl: '{{n}} naghihintay' },
  'Where it went': { he: 'לאן זה הלך', ur: 'کہاں خرچ ہوا', tl: 'Saan napunta' },
  'Projected next month': {
    he: 'צפי לחודש הבא',
    ur: 'اگلے مہینے کا تخمینہ',
    tl: 'Tantiya sa susunod na buwan',
  },
  // Suggestion kind labels (Live Home Section D).
  'Unreconciled cash': {
    he: 'מזומן לא מיושב',
    ur: 'غیر تصفیہ شدہ نقدی',
    tl: 'Hindi pa naitutugmang cash',
  },
  'Dormant loan': { he: 'הלוואה רדומה', ur: 'غیر فعال قرض', tl: 'Tulog na pautang' },
  'Unused note': { he: 'הערה שלא בשימוש', ur: 'غیر استعمال شدہ نوٹ', tl: 'Hindi ginamit na tala' },
  'Delegate work': { he: 'האצלת עבודה', ur: 'کام سونپیں', tl: 'Italaga ang trabaho' },
  'Unusual spend': { he: 'הוצאה חריגה', ur: 'غیر معمولی خرچ', tl: 'Hindi pangkaraniwang gastos' },
  'Ready to pay': { he: 'מוכן לתשלום', ur: 'ادائیگی کے لیے تیار', tl: 'Handa nang bayaran' },
  'Not started': { he: 'לא התחיל', ur: 'شروع نہیں ہوا', tl: 'Hindi pa sinisimulan' },
  Checked: { he: 'נבדק', ur: 'جانچ لیا', tl: 'Nasuri' },
  'Start this month': {
    he: 'התחל את החודש',
    ur: 'یہ مہینہ شروع کریں',
    tl: 'Simulan ang buwang ito',
  },
  'Open payroll': { he: 'פתח שכר', ur: 'پے رول کھولیں', tl: 'Buksan ang payroll' },
  Total: { he: 'סה"כ', ur: 'کل', tl: 'Kabuuan' },
  Who: { he: 'מי', ur: 'کون', tl: 'Sino' },
  Category: { he: 'קטגוריה', ur: 'زمرہ', tl: 'Kategorya' },
  'Which month?': { he: 'איזה חודש?', ur: 'کون سا مہینہ؟', tl: 'Aling buwan?' },

  // === Tasks ===
  'To do': { he: 'לעשות', ur: 'کرنے کے لیے', tl: 'Gagawin' },
  Week: { he: 'שבוע', ur: 'ہفتہ', tl: 'Linggo' },
  Search: { he: 'חיפוש', ur: 'تلاش', tl: 'Maghanap' },
  'All work': { he: 'הכול', ur: 'تمام کام', tl: 'Lahat ng gawain' },
  Running: { he: 'בעבודה', ur: 'جاری', tl: 'Ginagawa' },
  'High priority': { he: 'דחוף', ur: 'اہم', tl: 'Mahalaga' },
  'Nothing open right now.': {
    he: 'אין כרגע שום דבר פתוח.',
    ur: 'ابھی کچھ کھلا نہیں ہے۔',
    tl: 'Walang bukas ngayon.',
  },
  'Nothing finished yet.': {
    he: 'עוד לא הסתיים כלום.',
    ur: 'ابھی کچھ مکمل نہیں ہوا۔',
    tl: 'Wala pang natatapos.',
  },
  'Late · due {{when}}': {
    he: 'מאחר · היה אמור {{when}}',
    ur: 'تاخیر · مقررہ {{when}}',
    tl: 'Huli · dapat {{when}}',
  },
  Working: { he: 'עובד/ת', ur: 'کام کر رہے ہیں', tl: 'Nagtatrabaho' },
  'Waiting for work': { he: 'מחכה לעבודה', ur: 'کام کا انتظار', tl: 'Naghihintay ng gawain' },
  '{{n}} to do': { he: '{{n}} לעשות', ur: '{{n}} کرنے ہیں', tl: '{{n}} ang gagawin' },
  'The week, on one screen': {
    he: 'השבוע, במסך אחד',
    ur: 'پورا ہفتہ، ایک اسکرین پر',
    tl: 'Ang linggo, sa isang screen',
  },
  'Set what repeats': {
    he: 'קבע מה חוזר',
    ur: 'طے کریں کیا دہرایا جائے',
    tl: 'Itakda kung ano ang paulit-ulit',
  },
  Nothing: { he: 'כלום', ur: 'کچھ نہیں', tl: 'Wala' },

  // === People ===
  Attention: { he: 'דורש טיפול', ur: 'توجہ طلب', tl: 'Kailangan ng atensyon' },
  Trial: { he: 'תקופת ניסיון', ur: 'آزمائشی', tl: 'Subok' },
  Managers: { he: 'בעלי הרשאות', ur: 'اختیار رکھنے والے', tl: 'May pahintulot' },
  'Working now': { he: 'עובדים עכשיו', ur: 'ابھی کام پر', tl: 'Nagtatrabaho ngayon' },
  'Everyone else': { he: 'כל השאר', ur: 'باقی سب', tl: 'Lahat ng iba' },
  'No position set': {
    he: 'לא הוגדר תפקיד',
    ur: 'عہدہ مقرر نہیں',
    tl: 'Walang nakatakdang posisyon',
  },
  'Has not set their own password yet': {
    he: 'עוד לא קבע/ה סיסמה משלו/ה',
    ur: 'ابھی اپنا پاس ورڈ مقرر نہیں کیا',
    tl: 'Hindi pa naglalagay ng sariling password',
  },
  Access: { he: 'הרשאות', ur: 'رسائی', tl: 'Access' },
  "Today's progress": { he: 'ההתקדמות היום', ur: 'آج کی پیش رفت', tl: 'Progreso ngayon' },

  // === Person page ===
  'Person not found': { he: 'האדם לא נמצא', ur: 'فرد نہیں ملا', tl: 'Hindi natagpuan ang tao' },
  Call: { he: 'התקשר', ur: 'کال کریں', tl: 'Tawagan' },
  Message: { he: 'הודעה', ur: 'پیغام', tl: 'Mensahe' },
  Assign: { he: 'הטל משימה', ur: 'کام دیں', tl: 'Magbigay ng gawain' },
  Progress: { he: 'התקדמות', ur: 'پیش رفت', tl: 'Progreso' },
  'Their work': { he: 'העבודה שלו/ה', ur: 'ان کا کام', tl: 'Ang kanilang trabaho' },
  Pay: { he: 'שכר', ur: 'تنخواہ', tl: 'Sahod' },
  'Per day': { he: 'ליום', ur: 'یومیہ', tl: 'Kada araw' },
  'Per hour': { he: 'לשעה', ur: 'فی گھنٹہ', tl: 'Kada oras' },
  'On trial — no pay agreed yet': {
    he: 'בתקופת ניסיון — עוד לא סוכם שכר',
    ur: 'آزمائشی مدت — تنخواہ ابھی طے نہیں',
    tl: 'Nasa subok — wala pang napagkasunduang sahod',
  },
  'No pay set yet': {
    he: 'עוד לא נקבע שכר',
    ur: 'تنخواہ ابھی مقرر نہیں',
    tl: 'Wala pang nakatakdang sahod',
  },
  Account: { he: 'חשבון', ur: 'اکاؤنٹ', tl: 'Account' },

  // === Task page ===
  Status: { he: 'מצב', ur: 'حالت', tl: 'Katayuan' },
  Instructions: { he: 'הוראות', ur: 'ہدایات', tl: 'Mga tagubilin' },
  'Execution proof': { he: 'הוכחת ביצוע', ur: 'کام کا ثبوت', tl: 'Patunay ng pagkakagawa' },
  Reference: { he: 'רפרנס', ur: 'حوالہ', tl: 'Sanggunian' },
  Required: { he: 'חובה', ur: 'لازمی', tl: 'Kailangan' },
  'Add proof': { he: 'הוסף הוכחה', ur: 'ثبوت شامل کریں', tl: 'Magdagdag ng patunay' },
  Location: { he: 'מיקום', ur: 'مقام', tl: 'Lokasyon' },
  Updates: { he: 'עדכונים', ur: 'اپ ڈیٹس', tl: 'Mga update' },
  'Full history': { he: 'היסטוריה מלאה', ur: 'مکمل تاریخ', tl: 'Buong kasaysayan' },
  Related: { he: 'קשור', ur: 'متعلقہ', tl: 'Kaugnay' },
  'Send update…': { he: 'שלח עדכון…', ur: 'اپ ڈیٹ بھیجیں…', tl: 'Magpadala ng update…' },
  Actions: { he: 'פעולות', ur: 'کارروائیاں', tl: 'Mga aksyon' },
  'No deadline': { he: 'אין מועד יעד', ur: 'کوئی آخری وقت نہیں', tl: 'Walang takdang oras' },
  'Proof required': { he: 'נדרשת הוכחה', ur: 'ثبوت درکار ہے', tl: 'Kailangan ng patunay' },
  You: { he: 'אתה', ur: 'آپ', tl: 'Ikaw' },
  Unknown: { he: 'לא ידוע', ur: 'نامعلوم', tl: 'Hindi kilala' },
  Listen: { he: 'האזן', ur: 'سنیں', tl: 'Pakinggan' },
  '{{done}} of {{total}} steps done': {
    he: '{{done}} מתוך {{total}} שלבים הושלמו',
    ur: '{{total}} میں سے {{done}} مراحل مکمل',
    tl: '{{done}} sa {{total}} hakbang tapos na',
  },
  '1 proof sent': {
    he: 'הוכחה אחת נשלחה',
    ur: 'ایک ثبوت بھیجا گیا',
    tl: 'May 1 patunay na naipadala',
  },
  '{{n}} proofs sent': {
    he: '{{n}} הוכחות נשלחו',
    ur: '{{n}} ثبوت بھیجے گئے',
    tl: '{{n}} patunay ang naipadala',
  },
  behind: { he: 'באיחור', ur: 'تاخیر سے', tl: 'huli' },
  '1 message': { he: 'הודעה אחת', ur: 'ایک پیغام', tl: '1 mensahe' },
  '{{n}} messages': { he: '{{n}} הודעות', ur: '{{n}} پیغامات', tl: '{{n}} mensahe' },
  'Translated to {{lang}} for {{name}}': {
    he: 'תורגם ל{{lang}} עבור {{name}}',
    ur: '{{name}} کے لیے {{lang}} میں ترجمہ ہوا',
    tl: 'Isinalin sa {{lang}} para kay {{name}}',
  },
  '{{n}} min': { he: '{{n}} דק׳', ur: '{{n}} منٹ', tl: '{{n}} min' },
  '{{h}}h': { he: '{{h}} ש׳', ur: '{{h}} گھنٹے', tl: '{{h}}h' },
  '{{h}}h {{m}}m': { he: '{{h}} ש׳ {{m}} דק׳', ur: '{{h}} گھنٹے {{m}} منٹ', tl: '{{h}}h {{m}}m' },
  Due: { he: 'מועד', ur: 'مقررہ وقت', tl: 'Takda' },
  'Due today': { he: 'להיום', ur: 'آج تک', tl: 'Ngayong araw' },
  'Due tomorrow': { he: 'למחר', ur: 'کل تک', tl: 'Bukas' },

  // === Staff today ===
  'Coming next': { he: 'הבא בתור', ur: 'اگلا', tl: 'Susunod' },
  '{{n}} tasks today': { he: '{{n}} משימות היום', ur: 'آج {{n}} کام', tl: '{{n}} gawain ngayon' },
  '{{done}} of {{total}} tasks': {
    he: '{{done}} מתוך {{total}} משימות',
    ur: '{{total}} میں سے {{done}} کام',
    tl: '{{done}} sa {{total}} gawain',
  },
  'Completed today': { he: 'הושלמו היום', ur: 'آج مکمل ہوئے', tl: 'Natapos ngayong araw' },
  'Report issue': { he: 'דיווח על תקלה', ur: 'مسئلہ رپورٹ کریں', tl: 'Mag-ulat ng problema' },
  'What is wrong?': { he: 'מה הבעיה?', ur: 'کیا مسئلہ ہے؟', tl: 'Ano ang problema?' },
  'Start work': { he: 'התחל לעבוד', ur: 'کام شروع کریں', tl: 'Simulan ang trabaho' },
  'Photo proof required': {
    he: 'נדרשת תמונה כהוכחה',
    ur: 'تصویری ثبوت لازمی ہے',
    tl: 'Kailangan ng larawang patunay',
  },
  'Was due': { he: 'היה אמור', ur: 'مقررہ وقت تھا', tl: 'Dapat noong' },
  'Start at {{time}}': {
    he: 'להתחיל ב־{{time}}',
    ur: '{{time}} پر شروع',
    tl: 'Simulan sa {{time}}',
  },
  'No time set': { he: 'לא נקבעה שעה', ur: 'وقت مقرر نہیں', tl: 'Walang oras na nakatakda' },

  // === Staff history ===
  Filter: { he: 'סינון', ur: 'فلٹر', tl: 'Salain' },
  'This week': { he: 'השבוע', ur: 'اس ہفتے', tl: 'Ngayong linggo' },
  'On time': { he: 'בזמן', ur: 'وقت پر', tl: 'Nasa oras' },
  '1 task': { he: 'משימה אחת', ur: 'ایک کام', tl: '1 gawain' },
  '{{n}} tasks': { he: '{{n}} משימות', ur: '{{n}} کام', tl: '{{n}} gawain' },
  'Search your work…': {
    he: 'חיפוש בעבודה שלך…',
    ur: 'اپنے کام میں تلاش کریں…',
    tl: 'Hanapin ang iyong trabaho…',
  },
  'Nothing matches that.': {
    he: 'אין תוצאות מתאימות.',
    ur: 'اس سے کچھ میل نہیں کھاتا۔',
    tl: 'Walang tumugma.',
  },
  'Proof accepted': { he: 'ההוכחה התקבלה', ur: 'ثبوت قبول ہوا', tl: 'Tinanggap ang patunay' },
  'Finished late ({{time}})': {
    he: 'הסתיים באיחור ({{time}})',
    ur: 'دیر سے مکمل ({{time}})',
    tl: 'Natapos nang huli ({{time}})',
  },
  '{{state}} at {{time}}': {
    he: '{{state}} ב־{{time}}',
    ur: '{{time}} پر {{state}}',
    tl: '{{state}} sa {{time}}',
  },

  // === My pay ===
  'Not opened yet': { he: 'עוד לא נפתח', ur: 'ابھی نہیں کھلا', tl: 'Hindi pa binubuksan' },
  'Being prepared': { he: 'בהכנה', ur: 'تیار ہو رہا ہے', tl: 'Inihahanda' },
  'Total earned this month': {
    he: 'סך ההשתכרות החודש',
    ur: 'اس مہینے کی کل کمائی',
    tl: 'Kabuuang kinita ngayong buwan',
  },
  'Your agreed rate': { he: 'השכר שסוכם', ur: 'طے شدہ تنخواہ', tl: 'Napagkasunduang sahod' },
  'Month progress': { he: 'התקדמות החודש', ur: 'مہینے کی پیش رفت', tl: 'Progreso ng buwan' },
  'Day {{d}} of {{n}}': {
    he: 'יום {{d}} מתוך {{n}}',
    ur: '{{n}} میں سے دن {{d}}',
    tl: 'Araw {{d}} sa {{n}}',
  },
  'Pay breakdown': { he: 'פירוט השכר', ur: 'تنخواہ کی تفصیل', tl: 'Detalye ng sahod' },
  'Hours worked': { he: 'שעות עבודה', ur: 'کام کے گھنٹے', tl: 'Oras na trabaho' },
  Month: { he: 'חודש', ur: 'مہینہ', tl: 'Buwan' },
  '{{h}}h of that is overtime': {
    he: '{{h}} שעות מתוכן שעות נוספות',
    ur: 'ان میں سے {{h}} گھنٹے اوور ٹائم ہیں',
    tl: '{{h}}h nito ay overtime',
  },
  'Hide the days': { he: 'הסתר את הימים', ur: 'دن چھپائیں', tl: 'Itago ang mga araw' },
  'Show every day': { he: 'הצג כל יום', ur: 'ہر دن دکھائیں', tl: 'Ipakita ang bawat araw' },
  'Leave balance': { he: 'יתרת חופשה', ur: 'چھٹیوں کا توازن', tl: 'Natitirang bakasyon' },
  'Vacation remaining': { he: 'ימי חופשה שנותרו', ur: 'باقی چھٹیاں', tl: 'Natitirang bakasyon' },
  '{{n}} days': { he: '{{n}} ימים', ur: '{{n}} دن', tl: '{{n}} araw' },
  'Book a day off': {
    he: 'בקשת יום חופש',
    ur: 'چھٹی کا دن رکھیں',
    tl: 'Mag-book ng araw ng pahinga',
  },
  'Past payslips': { he: 'תלושים קודמים', ur: 'پچھلی تنخواہ پرچیاں', tl: 'Mga nakaraang payslip' },
  '{{amount}} still owed': {
    he: 'נותרו {{amount}}',
    ur: '{{amount}} ابھی باقی',
    tl: '{{amount}} pa ang utang',
  },

  // === More / notifications / house rules ===
  House: { he: 'הבית', ur: 'گھر', tl: 'Bahay' },
  'Running the house': { he: 'ניהול הבית', ur: 'گھر چلانا', tl: 'Pagpapatakbo ng bahay' },
  'Emergency contacts': {
    he: 'טלפונים לחירום',
    ur: 'ہنگامی رابطے',
    tl: 'Mga contact sa emergency',
  },
  'My profile': { he: 'הפרופיל שלי', ur: 'میری پروفائل', tl: 'Aking profile' },
  Unread: { he: 'לא נקראו', ur: 'اَن پڑھے', tl: 'Hindi pa nababasa' },
  'Search the rules…': {
    he: 'חיפוש בכללים…',
    ur: 'قواعد میں تلاش کریں…',
    tl: 'Hanapin sa mga panuntunan…',
  },
  'All rules': { he: 'כל הכללים', ur: 'تمام قواعد', tl: 'Lahat ng panuntunan' },
  Everyone: { he: 'כולם', ur: 'سب', tl: 'Lahat' },

  // === Profile ===
  Owner: { he: 'בעלים', ur: 'مالک', tl: 'May-ari' },
  'Personal information': { he: 'פרטים אישיים', ur: 'ذاتی معلومات', tl: 'Personal na impormasyon' },
  'Email address': { he: 'כתובת אימייל', ur: 'ای میل پتہ', tl: 'Email address' },
  'Phone number': { he: 'מספר טלפון', ur: 'فون نمبر', tl: 'Numero ng telepono' },
  'Not set': { he: 'לא הוגדר', ur: 'مقرر نہیں', tl: 'Hindi nakatakda' },
  'Edit personal details': {
    he: 'עריכת הפרטים האישיים',
    ur: 'ذاتی تفصیلات میں ترمیم',
    tl: 'I-edit ang personal na detalye',
  },
  Preferences: { he: 'העדפות', ur: 'ترجیحات', tl: 'Mga kagustuhan' },
  'App language': { he: 'שפת האפליקציה', ur: 'ایپ کی زبان', tl: 'Wika ng app' },
  'Navigation app': { he: 'אפליקציית ניווט', ur: 'نیویگیشن ایپ', tl: 'Navigation app' },
  On: { he: 'פעיל', ur: 'آن', tl: 'Naka-on' },
  Off: { he: 'כבוי', ur: 'آف', tl: 'Naka-off' },
  Security: { he: 'אבטחה', ur: 'سیکیورٹی', tl: 'Seguridad' },
  'Push notifications': { he: 'התראות דחיפה', ur: 'پش نوٹیفیکیشنز', tl: 'Push notifications' },

  // === Event page ===
  'Happening now': { he: 'קורה עכשיו', ur: 'ابھی جاری ہے', tl: 'Nangyayari ngayon' },
  Over: { he: 'הסתיים', ur: 'ختم', tl: 'Tapos na' },
  Kind: { he: 'סוג', ur: 'قسم', tl: 'Uri' },
  'Before it starts': { he: 'לפני שזה מתחיל', ur: 'شروع ہونے سے پہلے', tl: 'Bago magsimula' },
  'While it runs': { he: 'בזמן האירוע', ur: 'دوران', tl: 'Habang tumatakbo' },
  'People involved': { he: 'מי מעורב', ur: 'شامل لوگ', tl: 'Mga taong kasama' },
  '1 person': { he: 'אדם אחד', ur: 'ایک فرد', tl: '1 tao' },

  // === Sign in / setup ===
  'The art of household management.': {
    he: 'האמנות של ניהול הבית.',
    ur: 'گھر کے انتظام کا فن۔',
    tl: 'Ang sining ng pamamahala ng tahanan.',
  },
  'Enter details': { he: 'הזן פרטים', ur: 'تفصیلات درج کریں', tl: 'Ilagay ang detalye' },
  'One more thing': { he: 'עוד דבר אחד', ur: 'ایک اور بات', tl: 'Isa pang bagay' },
  'Your household': { he: 'משק הבית שלך', ur: 'آپ کا گھرانہ', tl: 'Ang iyong sambahayan' },
  // === Toasts, undo, handover ===
  Undo: { he: 'ביטול', ur: 'واپس', tl: 'I-undo' },
  Unpinned: { he: 'הוסר מהצמדה', ur: 'پن ہٹا دیا', tl: 'Inalis sa pin' },
  'I have written it down': {
    he: 'רשמתי',
    ur: 'میں نے لکھ لیا',
    tl: 'Naisulat ko na',
  },
  Generate: { he: 'צור', ur: 'بنائیں', tl: 'Bumuo' },
  'Their current password stops working the moment you do this.': {
    he: 'הסיסמה הנוכחית שלהם מפסיקה לעבוד ברגע שתעשה את זה.',
    ur: 'ایسا کرتے ہی ان کا موجودہ پاس ورڈ کام کرنا بند کر دے گا۔',
    tl: 'Titigil na gumana ang kasalukuyang password nila sa sandaling gawin mo ito.',
  },

  'Four answers and you are in.': {
    he: 'ארבע תשובות ואתה בפנים.',
    ur: 'چار جواب اور آپ اندر ہیں۔',
    tl: 'Apat na sagot at pasok ka na.',
  },

  // === Activity log ===
  Activity: { he: 'פעילות', ur: 'سرگرمی', tl: 'Aktibidad' },
  'Activity log': { he: 'יומן פעילות', ur: 'سرگرمی کا ریکارڈ', tl: 'Tala ng aktibidad' },
  'Everything that happened in this household, and who did it.': {
    he: 'כל מה שקרה בבית הזה, ומי עשה את זה.',
    ur: 'اس گھر میں جو کچھ ہوا، اور کس نے کیا۔',
    tl: 'Lahat ng nangyari sa tahanang ito, at kung sino ang gumawa.',
  },
  'Nothing recorded yet.': {
    he: 'עדיין לא נרשם דבר.',
    ur: 'ابھی تک کچھ درج نہیں ہوا۔',
    tl: 'Wala pang naitala.',
  },
  'Someone no longer here': {
    he: 'מישהו שכבר לא כאן',
    ur: 'کوئی جو اب یہاں نہیں',
    tl: 'Isang taong wala na rito',
  },
  'Showing the most recent 200 entries.': {
    he: 'מוצגות 200 הרשומות האחרונות.',
    ur: 'حالیہ 200 اندراجات دکھائے جا رہے ہیں۔',
    tl: 'Ipinapakita ang pinakahuling 200 tala.',
  },
  // Not "Access" — that key already means the permission switches.
  'Sign-ins': { he: 'כניסות', ur: 'سائن ان', tl: 'Mga sign-in' },
  Changes: { he: 'שינויים', ur: 'تبدیلیاں', tl: 'Mga pagbabago' },
  Work: { he: 'עבודה', ur: 'کام', tl: 'Trabaho' },
  created: { he: 'יצר', ur: 'بنایا', tl: 'gumawa' },
  changed: { he: 'שינה', ur: 'تبدیل کیا', tl: 'binago' },
  removed: { he: 'מחק', ur: 'ہٹایا', tl: 'inalis' },
  moved: { he: 'קידם', ur: 'منتقل کیا', tl: 'inilipat' },
  'signed in': { he: 'נכנס', ur: 'سائن ان ہوا', tl: 'nag-sign in' },
  'signed out': { he: 'יצא', ur: 'سائن آؤٹ ہوا', tl: 'nag-sign out' },
  invited: { he: 'הזמין', ur: 'دعوت دی', tl: 'nag-imbita' },
  'reset a password': {
    he: 'איפס סיסמה',
    ur: 'پاس ورڈ ری سیٹ کیا',
    tl: 'nag-reset ng password',
  },
  translated: { he: 'תרגם', ur: 'ترجمہ کیا', tl: 'isinalin' },
  person: { he: 'אדם', ur: 'شخص', tl: 'tao' },
  household: { he: 'בית', ur: 'گھرانہ', tl: 'sambahayan' },
  routine: { he: 'שגרה', ur: 'معمول', tl: 'rutina' },
  receipt: { he: 'קבלה', ur: 'رسید', tl: 'resibo' },
  loan: { he: 'הלוואה', ur: 'قرض', tl: 'utang' },
  reimbursement: { he: 'החזר', ur: 'واپسی', tl: 'reimbursement' },
  payslip: { he: 'תלוש', ur: 'تنخواہ پرچی', tl: 'payslip' },
  'staff invite': { he: 'הזמנת עובד', ur: 'عملے کی دعوت', tl: 'imbitasyon sa tauhan' },
  'co-owner invite': {
    he: 'הזמנת בעלים שותף',
    ur: 'شریک مالک کی دعوت',
    tl: 'imbitasyon sa co-owner',
  },
  'IP address': { he: 'כתובת IP', ur: 'آئی پی ایڈریس', tl: 'IP address' },
  Device: { he: 'מכשיר', ur: 'آلہ', tl: 'Device' },

  // === Deciding from the inbox ===
  'Send rejection': { he: 'שלח דחייה', ur: 'رد بھیجیں', tl: 'Ipadala ang pagtanggi' },

  // === Bills the household already knows about ===
  'Coming up': { he: 'בקרוב', ur: 'آنے والا', tl: 'Paparating' },
  'Add a bill': { he: 'הוסף חשבון', ur: 'بل شامل کریں', tl: 'Magdagdag ng bayarin' },
  'One-off': { he: 'חד-פעמי', ur: 'ایک بار', tl: 'Minsanan' },
  'Every month': { he: 'כל חודש', ur: 'ہر مہینے', tl: 'Buwan-buwan' },
  'Every quarter': { he: 'כל רבעון', ur: 'ہر سہ ماہی', tl: 'Kada quarter' },
  'Every year': { he: 'כל שנה', ur: 'ہر سال', tl: 'Taon-taon' },
  'In {{n}} days': { he: 'בעוד {{n}} ימים', ur: '{{n}} دن میں', tl: 'Sa {{n}} araw' },
  'Marked paid': { he: 'סומן כשולם', ur: 'ادا شدہ نشان زد', tl: 'Namarkahang bayad' },
  Removed: { he: 'הוסר', ur: 'ہٹا دیا', tl: 'Inalis' },
  Added: { he: 'נוסף', ur: 'شامل ہو گیا', tl: 'Naidagdag' },
  'Nothing written down yet.': {
    he: 'עדיין לא נרשם כלום.',
    ur: 'ابھی کچھ درج نہیں کیا گیا۔',
    tl: 'Wala pang naisulat.',
  },
  'The pool service, a visa renewal — anything the app cannot work out on its own.': {
    he: 'שירות הבריכה, חידוש ויזה — כל מה שהאפליקציה לא יכולה להסיק לבד.',
    ur: 'پول سروس، ویزا کی تجدید — ہر وہ چیز جو ایپ خود اندازہ نہیں لگا سکتی۔',
    tl: 'Ang pool service, pag-renew ng visa — anumang hindi kayang hulaan ng app.',
  },
  'What is it? e.g. Pool service': {
    he: 'מה זה? למשל שירות בריכה',
    ur: 'یہ کیا ہے؟ مثلاً پول سروس',
    tl: 'Ano ito? hal. Pool service',
  },

  // === End of service ===
  'If everyone left today': {
    he: 'אם כולם היו עוזבים היום',
    ur: 'اگر سب آج چلے جائیں',
    tl: 'Kung aalis lahat ngayon',
  },
  'End of service (estimate)': {
    he: 'גמר חשבון (הערכה)',
    ur: 'اختتامِ ملازمت (تخمینہ)',
    tl: 'Katapusan ng serbisyo (tantiya)',
  },
  'End of service': {
    he: 'גמר חשבון',
    ur: 'اختتامِ ملازمت',
    tl: 'Katapusan ng serbisyo',
  },
  '{{d}} days a year, after {{q}} days': {
    he: '{{d}} ימים בשנה, אחרי {{q}} ימים',
    ur: 'سالانہ {{d}} دن، {{q}} دن کے بعد',
    tl: '{{d}} araw bawat taon, pagkatapos ng {{q}} araw',
  },
  '{{n}} yrs': { he: '{{n}} שנים', ur: '{{n}} سال', tl: '{{n}} taon' },
  // === Payslip you can keep ===
  'Save as PDF': {
    he: 'שמירה כ-PDF',
    ur: 'پی ڈی ایف کے طور پر محفوظ کریں',
    tl: 'I-save bilang PDF',
  },
  'Payslip not found': {
    he: 'התלוש לא נמצא',
    ur: 'پرچی نہیں ملی',
    tl: 'Hindi natagpuan ang payslip',
  },
  Earnings: { he: 'תשלומים', ur: 'کمائی', tl: 'Kita' },
  Absence: { he: 'היעדרות', ur: 'غیر حاضری', tl: 'Pagliban' },
  Adjustments: { he: 'התאמות', ur: 'ایڈجسٹمنٹ', tl: 'Mga pagsasaayos' },
  Advance: { he: 'מקדמה', ur: 'پیشگی', tl: 'Paunang bayad' },
  'Not yet paid': { he: 'טרם שולם', ur: 'ابھی ادا نہیں ہوا', tl: 'Hindi pa nababayaran' },

  'Saved — it will send when you have signal.': {
    he: 'נשמר — יישלח כשתהיה קליטה.',
    ur: 'محفوظ ہو گیا — سگنل آنے پر بھیج دیا جائے گا۔',
    tl: 'Naka-save — ipapadala kapag may signal.',
  },
  '{{n}} waiting to send': {
    he: '{{n}} ממתינים לשליחה',
    ur: '{{n}} بھیجنے کے منتظر',
    tl: '{{n}} ang naghihintay maipadala',
  },

  Appearance: { he: 'מראה', ur: 'ظاہری شکل', tl: 'Anyo' },
  Light: { he: 'בהיר', ur: 'روشن', tl: 'Maliwanag' },
  Dark: { he: 'כהה', ur: 'گہرا', tl: 'Madilim' },
  'Match my phone': {
    he: 'לפי הטלפון',
    ur: 'میرے فون کے مطابق',
    tl: 'Sundan ang telepono',
  },

  // === Clock in, clock out ===
  "I'm here": { he: 'הגעתי', ur: 'میں یہاں ہوں', tl: 'Nandito na ako' },
  "I'm done": { he: 'סיימתי', ur: 'میں فارغ ہوں', tl: 'Tapos na ako' },
  'Tap when you arrive': {
    he: 'הקש כשאת מגיעה',
    ur: 'پہنچنے پر ٹیپ کریں',
    tl: 'I-tap pagdating mo',
  },
  'Working for {{time}}': {
    he: 'בעבודה כבר {{time}}',
    ur: '{{time}} سے کام پر',
    tl: 'Nagtatrabaho nang {{time}}',
  },
  'Clocked in': { he: 'נרשמה כניסה', ur: 'حاضری درج ہوئی', tl: 'Naka-clock in' },
  'Clocked out': { he: 'נרשמה יציאה', ur: 'روانگی درج ہوئی', tl: 'Naka-clock out' },

  // === Search, and doing several at once ===
  'People, tasks, rules, money…': {
    he: 'אנשים, משימות, כללים, כסף…',
    ur: 'لوگ، کام، اصول، پیسہ…',
    tl: 'Mga tao, gawain, panuntunan, pera…',
  },
  'Type at least two letters.': {
    he: 'הקלד לפחות שתי אותיות.',
    ur: 'کم از کم دو حروف لکھیں۔',
    tl: 'Mag-type ng hindi bababa sa dalawang letra.',
  },
  Receipts: { he: 'קבלות', ur: 'رسیدیں', tl: 'Mga resibo' },
  Events: { he: 'אירועים', ur: 'تقریبات', tl: 'Mga kaganapan' },
  'Select several': { he: 'בחירה מרובה', ur: 'کئی منتخب کریں', tl: 'Pumili ng ilan' },
  '{{n}} selected': { he: '{{n}} נבחרו', ur: '{{n}} منتخب', tl: '{{n}} ang napili' },
  '{{n}} updated': { he: '{{n}} עודכנו', ur: '{{n}} اپ ڈیٹ ہوئے', tl: '{{n}} ang na-update' },
  '{{done}} updated, {{failed}} could not be': {
    he: '{{done}} עודכנו, {{failed}} לא הצליחו',
    ur: '{{done}} اپ ڈیٹ ہوئے، {{failed}} نہیں ہو سکے',
    tl: '{{done}} ang na-update, {{failed}} ang hindi',
  },
  'Pick some tasks.': {
    he: 'בחר כמה משימות.',
    ur: 'کچھ کام منتخب کریں۔',
    tl: 'Pumili ng ilang gawain.',
  },
  'These are in different states — nothing applies to all of them.': {
    he: 'הן במצבים שונים — אין פעולה שמתאימה לכולן.',
    ur: 'یہ مختلف حالتوں میں ہیں — کوئی عمل سب پر لاگو نہیں ہوتا۔',
    tl: 'Magkakaiba ang kalagayan nila — walang aksyong akma sa lahat.',
  },
  'Cancelled in bulk': {
    he: 'בוטלו יחד',
    ur: 'اجتماعی طور پر منسوخ',
    tl: 'Kinansela nang sabay-sabay',
  },

  // === Your data ===
  'Your data': { he: 'הנתונים שלך', ur: 'آپ کا ڈیٹا', tl: 'Ang iyong data' },
  'Download everything': {
    he: 'הורדה של הכול',
    ur: 'سب کچھ ڈاؤن لوڈ کریں',
    tl: 'I-download ang lahat',
  },
  'One file with every task, receipt, payslip and message.': {
    he: 'קובץ אחד עם כל משימה, קבלה, תלוש והודעה.',
    ur: 'ایک فائل جس میں ہر کام، رسید، تنخواہ پرچی اور پیغام ہے۔',
    tl: 'Isang file na may bawat gawain, resibo, payslip at mensahe.',
  },
  Downloaded: { he: 'הורד', ur: 'ڈاؤن لوڈ ہو گیا', tl: 'Na-download' },
  'Export failed': { he: 'הייצוא נכשל', ur: 'ایکسپورٹ ناکام', tl: 'Nabigo ang pag-export' },
  'Delete this household': {
    he: 'מחיקת משק הבית',
    ur: 'یہ گھرانہ حذف کریں',
    tl: 'Burahin ang sambahayang ito',
  },
  'Everything, for everyone, permanently. There is no undo.': {
    he: 'הכול, לכולם, לצמיתות. אין דרך חזרה.',
    ur: 'سب کچھ، سب کے لیے، ہمیشہ کے لیے۔ واپسی ممکن نہیں۔',
    tl: 'Lahat, para sa lahat, tuluyan. Walang balikan.',
  },
  'Download a copy first — this cannot be reversed.': {
    he: 'הורד עותק קודם — אי אפשר לבטל את זה.',
    ur: 'پہلے ایک کاپی ڈاؤن لوڈ کریں — یہ واپس نہیں ہو سکتا۔',
    tl: 'Mag-download muna ng kopya — hindi ito mababawi.',
  },
  'Type the household name': {
    he: 'הקלד את שם משק הבית',
    ur: 'گھرانے کا نام لکھیں',
    tl: 'I-type ang pangalan ng sambahayan',
  },
  'Your password': { he: 'הסיסמה שלך', ur: 'آپ کا پاس ورڈ', tl: 'Ang iyong password' },

  '{{n}} people are missing a start date or pay terms, so they are not counted.': {
    he: 'ל-{{n}} אנשים חסר תאריך תחילה או תנאי שכר, ולכן הם לא נספרים.',
    ur: '{{n}} افراد کی تاریخِ آغاز یا تنخواہ کی شرائط موجود نہیں، اس لیے وہ شمار نہیں ہوئے۔',
    tl: '{{n}} tao ang walang petsa ng simula o kasunduan sa sahod, kaya hindi sila binibilang.',
  },
};

export function t(key: string): string {
  const lang = getLang();
  if (lang === 'en') return key;
  return T[key]?.[lang] ?? key;
}

// Format helpers that respect the active language.
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '';
  const lang = getLang();
  const locale =
    lang === 'he' ? 'he-IL' : lang === 'ur' ? 'ur-PK' : lang === 'tl' ? 'fil-PH' : 'en-US';
  try {
    return new Date(iso).toLocaleString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    });
  } catch {
    return iso;
  }
}

// On boot, apply lang/dir to <html>
export function bootstrapLang(): void {
  const l = getLang();
  document.documentElement.lang = l;
  document.documentElement.dir = isRtl(l) ? 'rtl' : 'ltr';
}

/**
 * Pick the viewer's preferred translation from a per-language map, falling
 * back to the canonical source string when the slot is missing or empty.
 *
 * Used for owner-typed content (operation title / description) where the
 * server-side Haiku translator populates op.titleTranslations / op.descriptionTranslations
 * fire-and-forget on create. During the ~1-2s window between create and the
 * translator finishing, the slot may be empty for non-source langs — this
 * helper returns the source string so the staff member sees *something*
 * meaningful instead of a blank label. After the next refresh, the slot is
 * populated and they see their preferred lang.
 */
export function pickTranslation(
  translations: Record<string, string | null | undefined> | null | undefined,
  fallback: string,
  lang: Lang = getLang(),
): string {
  if (!translations) return fallback;
  const slot = translations[lang];
  if (slot && slot.trim().length > 0) return slot;
  return fallback;
}
